import { useState, useEffect } from "react";
import axios from "../api/axios";
import { hasAnyRole } from "../utils/auth";

const buildProtectedDocumentUrl = (documentPath) => {
  if (!documentPath) {
    return "#";
  }

  if (/^https?:\/\//i.test(documentPath)) {
    return documentPath;
  }

  const token = localStorage.getItem("token") || "";
  return `${documentPath}?token=${encodeURIComponent(token)}`;
};

// Best-available year signal used to order listing tables latest-first.
const recordSortRank = (record) => {
  if (!record) return -Infinity;
  const yearFromAcademic = (ay) => {
    if (!ay) return null;
    const m = String(ay).match(/(\d{4})/);
    if (m) return parseInt(m[1], 10);
    // Handle 2-digit academic year like "25-26" -> 2025.
    const m2 = String(ay).match(/(\d{2})/);
    return m2 ? 2000 + parseInt(m2[1], 10) : null;
  };
  const yearFromDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt.getFullYear();
  };
  // Primary = academic year; secondary = best real content year (NOT created_at,
  // which would order by "recently updated" rather than by year).
  const academicYear = yearFromAcademic(record.academic_year);
  const otherCandidates = [
    record.publication_year ? parseInt(record.publication_year, 10) : null,
    record.award_year ? parseInt(record.award_year, 10) : null,
    record.year ? parseInt(record.year, 10) : null,
    record.year_of_passing ? parseInt(record.year_of_passing, 10) : null,
    yearFromDate(record.date),
    yearFromDate(record.start_date),
    yearFromDate(record.date_of_filing),
    yearFromDate(record.date_of_registration),
    yearFromDate(record.consultancy_date),
  ].filter((y) => y !== null && !isNaN(y));
  const otherYear = otherCandidates.length ? Math.max(...otherCandidates) : null;
  const primaryYear = academicYear !== null ? academicYear : (otherYear !== null ? otherYear : 0);
  const secondaryYear = otherYear !== null ? otherYear : 0;
  const idPart = record.id ? Number(record.id) : 0;
  return primaryYear * 1e10 + secondaryYear * 1e5 + idPart;
};

// Return a copy of the list sorted latest-first.
const sortByYearDesc = (data) => {
  if (!Array.isArray(data)) return data;
  return [...data].sort((a, b) => recordSortRank(b) - recordSortRank(a));
};

// Three-state verification chip (green / red-rejected / yellow) with reason.
const VerificationStatusCell = ({ record }) => {
  const status = record?.verification_status || "unverified";
  const cls =
    status === "verified"
      ? "bg-green-100 text-green-800"
      : status === "rejected"
      ? "bg-red-100 text-red-800"
      : "bg-yellow-100 text-yellow-800";
  return (
    <div>
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status}</span>
      {status === "rejected" && record?.rejection_reason && (
        <div className="text-[11px] text-red-600 italic mt-1">Reason: {record.rejection_reason}</div>
      )}
    </div>
  );
};

// Academic year runs June–May. If today is June or later, the current
// academic year started this calendar year; otherwise it started last year.
// e.g. June 2026 -> "2026-27", any month in 2027 from June -> "2027-28".
const getCurrentAcademicStartYear = () => {
  const now = new Date();
  return now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
};

const buildAcademicYearOptions = (count = 20) => {
  const latestStartYear = getCurrentAcademicStartYear();

  return Array.from({ length: count }, (_, index) => {
    const startYear = latestStartYear - index;
    const endYear = String(startYear + 1).slice(-2);
    return `${startYear}-${endYear}`;
  });
};

const DEPARTMENT_OPTIONS = [
  "Engineering Sciences and Humanities",
  "Computer Science & Engineering - Data Science",
  "Artificial Intelligence",
  "Computer Science & Engineering (Cyber Security)",
  "Computer Science & Business Systems",
  "Computer Science & Engineering",
  "Information Technology",
  "Electrical Engineering",
  "Electronics & Telecommunication",
  "Mechanical Engineering",
  "Civil Engineering",
  "Industrial IoT",
  "Robotics & AI",
  "Career Development Cell",
];

const PROGRAM_OPTIONS = [
  "B.Tech",
  "M.Tech",
  "BCA",
  "MCA",
  "MBA",
  "BBA",
  "Other",
];

const ACADEMIC_YEAR_OPTIONS = buildAcademicYearOptions();

const SPONSORSHIP_TYPE_OPTIONS = [
  "Self Sponsored",
  "Institution Sponsored",
  "Sponsored by Government Agency",
  "Industry Sponsored",
  "Other",
];

const PATENT_TYPE_OPTIONS = ["Design", "Utility"];
const PATENT_STATUS_OPTIONS = ["Filed", "Published", "Granted"];
const DURATION_UNIT_OPTIONS = ["Days", "Weeks"];
const SEMESTER_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const COPYRIGHT_STATUS_OPTIONS = ["Filed", "Registered"];
const MEMBERSHIP_CATEGORY_OPTIONS = ["Professional Society", "Academic Society", "Scientific Body", "Other"];
const MEMBERSHIP_TYPE_OPTIONS = ["Annually", "Lifetime"];
const MOOC_CONTENT_OPTIONS = ["MOOC", "E-Content"];
const MOOC_SEMESTER_OPTIONS = SEMESTER_OPTIONS;

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Divorced", "Widowed"];
const CATEGORY_OPTIONS = ["Open", "OBC", "SC", "ST", "EWS", "Other"];
const CURRENT_DESIGNATION_OPTIONS = [
  "Professor",
  "Associate Professor",
  "Assistant Professor",
  "Teaching Assistant",
  "Senior Lecturer",
  "Lecturer",
  "Adjunct Faculty",
  "Visiting Faculty",
  "Other",
];
const TEACHING_DESIGNATION_OPTIONS = [
  "Professor",
  "Associate Professor",
  "Assistant Professor",
  "Teaching Assistant",
  "Senior Lecturer",
  "Lecturer",
  "Adjunct Faculty",
  "Visiting Faculty",
];
const TEACHING_STATUS_OPTIONS = [
  "University Approved",
  "Adhoc",
  "Management Regular",
];
const PUBLICATION_INDEXING_OPTIONS = ["WOS", "Scopus", "Scopus + WOS", "Other"];
const PUBLICATION_MULTI_INDEXING_OPTIONS = ["SCI", "SCIE", "ESCI", "SSCI"];
const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() - 1979 },
  (_, index) => String(new Date().getFullYear() - index)
);

const extractApiErrorMessage = (err, fallbackMessage) => {
  const responseData = err?.response?.data;

  if (responseData?.reason) {
    return `${fallbackMessage}: ${responseData.reason}`;
  }

  if (responseData?.message) {
    return `${fallbackMessage}: ${responseData.message}`;
  }

  return fallbackMessage;
};

const formatDateOnly = (value) => {
  if (!value) {
    return "";
  }

  const stringValue = String(value);
  // If it contains 'T' (ISO format from DB), extract just the date part directly
  // This avoids timezone offset issues that shift the date by -1 day
  if (stringValue.includes("T")) {
    return stringValue.split("T")[0];
  }
  // Already a plain date string like "2024-05-09"
  return stringValue;
};

function Profile() {
  const token = localStorage.getItem("token");
  // When an authorized user (super_admin / edit-permitted role) opens this page
  // with ?act_as=<userId>, all profile requests operate on that target user.
  const actAsUserId = new URLSearchParams(window.location.search).get("act_as");
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(actAsUserId ? { "X-Act-As-User": actAsUserId } : {}),
  };

  const [activeTab, setActiveTab] = useState("general");
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  // Date helper: Convert ISO to YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Show notification helper
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  // Initialize profile on mount
  useEffect(() => {
    axios.post("/profile/init", {}, { headers }).catch(() => {});
  }, []);

  const role = localStorage.getItem("role");
  const tabs = [
    { id: "general", label: "Personal Info", icon: "👤" },
    { id: "experience", label: "Experience", icon: "💼" },
    { id: "education", label: "Education", icon: "🎓" },
    { id: "academic", label: "Academic", icon: "📚" },
    { id: "research", label: "Research", icon: "🔬" },
    { id: "fdp", label: "FDP & Training", icon: "📖" },
    { id: "awards", label: "Awards & Industry", icon: "🏆" }
  ];

  // Super admin (acting as super_admin) has no personal profile to manage —
  // they can only edit OTHERS (via ?act_as). Block their own-profile view and
  // ask them to switch roles. When act_as is present they're editing someone
  // else, which stays allowed.
  if (hasAnyRole("super_admin") && !actAsUserId) {
    return (
      <div>
        <div className="max-w-3xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h1 className="text-xl font-bold text-amber-900 mb-1">No personal profile as Super Admin</h1>
            <p className="text-amber-800 text-sm">
              While acting as <span className="font-semibold">Super Admin</span> you don't manage a personal profile.
              You can edit other users' profiles from their report (via "Edit this Profile").
              To manage your own profile, switch to another role using the role menu at the top right.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-5">
          <h1 className="text-3xl font-bold text-gray-900">
            Faculty Profile
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Manage your professional information</p>
        </div>

        {/* Editing-on-behalf banner */}
        {actAsUserId && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl px-5 py-3 mb-5 flex items-center gap-3">
            <span className="text-xl">✏️</span>
            <div className="text-sm text-amber-900">
              <span className="font-semibold">You are editing another user's profile</span> (User ID: {actAsUserId}).
              Any changes you save here will be applied to that user's records.
            </div>
          </div>
        )}

        {/* Notification Toast */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg transition-all animate-fade-in ${
            notification.type === "success" ? "bg-green-600" : "bg-red-600"
          } text-white font-medium text-sm`}>
            {notification.message}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5 mb-5">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          {activeTab === "general" && <GeneralInfo headers={headers} showNotification={showNotification} section="personal" />}
          {activeTab === "experience" && <GeneralInfo headers={headers} showNotification={showNotification} section="experience" />}
          {activeTab === "education" && <EducationTab headers={headers} showNotification={showNotification} />}
          {activeTab === "academic" && <AcademicInfo headers={headers} showNotification={showNotification} />}
          {activeTab === "research" && <ResearchInfo headers={headers} showNotification={showNotification} />}
          {activeTab === "fdp" && <FDPTraining headers={headers} showNotification={showNotification} />}
          {activeTab === "awards" && <AwardsIndustry headers={headers} showNotification={showNotification} />}
        </div>

        {/* Step Navigation */}
        <div className="mt-5 flex justify-between items-center bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          {(() => {
            const tabIds = tabs.map((t) => t.id);
            const currentIndex = tabIds.indexOf(activeTab);
            const goPrev = () => setActiveTab(tabIds[Math.max(0, currentIndex - 1)]);
            const goNext = () => setActiveTab(tabIds[Math.min(tabIds.length - 1, currentIndex + 1)]);

            return (
              <>
                <div className="flex gap-2">
                  <button onClick={goPrev} disabled={currentIndex === 0} className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${currentIndex === 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                    ← Previous
                  </button>
                  <button onClick={goNext} disabled={currentIndex === tabIds.length - 1} className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${currentIndex === tabIds.length - 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
                    Next →
                  </button>
                </div>

                {currentIndex === tabIds.length - 1 ? (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post("/profile/submit", {}, { headers });
                        showNotification("Profile submitted successfully!");
                      } catch (err) {
                        showNotification(extractApiErrorMessage(err, "Failed to submit profile"), "error");
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-2 rounded-lg text-sm shadow-sm transition-all"
                  >
                    ✓ Submit Profile
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">Submit available on last tab</span>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/* ===============================================
   VERIFICATION LOGS TAB (SUPER ADMIN ONLY)
=============================================== */
// Logs tab removed. Standalone page created.

/* ===============================================
   GENERAL INFO TAB (WITHOUT EDUCATION)
=============================================== */
function GeneralInfo({ headers, showNotification, section = "personal" }) {
  const [personalForm, setPersonalForm] = useState({
    full_name: "", parent_name: "", department: "", current_designation: "",
    date_of_joining: "", date_of_birth: "", gender: "", marital_status: "", category: "",
    phone_number: "", aadhar_card: "", pan_card: "", address: ""
  });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [teachingForm, setTeachingForm] = useState({
    experience_type: "Teaching",
    college_name: "",
    designation: "",
    status: "",
    is_current_affiliated: false,
    date_of_entry: "",
    date_of_exit: ""
  });
  const [editingTeachingId, setEditingTeachingId] = useState(null);

  const [industrialForm, setIndustrialForm] = useState({
    company_name: "",
    designation: "",
    is_current_affiliated: false,
    date_of_entry: "",
    date_of_exit: ""
  });
  const [editingIndustryId, setEditingIndustryId] = useState(null);

  const [colleges, setColleges] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showOtherCollegeName, setShowOtherCollegeName] = useState(false);
  const [otherCollegeName, setOtherCollegeName] = useState("");
  const [showOtherCompanyName, setShowOtherCompanyName] = useState(false);
  const [otherCompanyName, setOtherCompanyName] = useState("");

  const [teachingExperiences, setTeachingExperiences] = useState([]);
  const [industryExperiences, setIndustryExperiences] = useState([]);

  useEffect(() => {
    loadPersonalDetails();
    loadPhoto();
    loadTeachingExperience();
    loadIndustryExperience();
    loadColleges();
    loadCompanies();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dateString.split('T')[0];
  };

  const computeYearsExperience = (entryDate, exitDate) => {
    if (!entryDate) return "";
    const start = new Date(entryDate);
    const end = exitDate ? new Date(exitDate) : new Date();
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return "";
    const years = (end - start) / (365.25 * 24 * 60 * 60 * 1000);
    return Number(years.toFixed(2));
  };

  const loadPersonalDetails = async () => {
    try {
      const res = await axios.get("/profile/personal-details", { headers });
      if (res.data) {
        setPersonalForm((prev) => ({
          ...prev,
          ...res.data,
          date_of_joining: formatDate(res.data.date_of_joining),
          date_of_birth: formatDate(res.data.date_of_birth)
        }));
        if (res.data.photo_url) {
          setPhotoUrl(res.data.photo_url);
        }
      }
    } catch (err) {}
  };

  const loadPhoto = async () => {
    try {
      const res = await axios.get("/profile/photo", { headers });
      if (res.data && res.data.photo_url) {
        setPhotoUrl(res.data.photo_url);
      }
    } catch (err) {}
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);

      const res = await axios.post("/profile/photo", formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });

      if (res.data.photo_url) {
        setPhotoUrl(res.data.photo_url);
        showNotification("Photo uploaded successfully!");
      }
    } catch (err) {
      showNotification(err.response?.data?.message || "Failed to upload photo", "error");
    } finally {
      setPhotoUploading(false);
    }
  };

  const loadTeachingExperience = async () => {
    try {
      const res = await axios.get("/profile/teaching-experience", { headers });
      setTeachingExperiences(res.data || []);
    } catch (err) {}
  };

  const loadIndustryExperience = async () => {
    try {
      const res = await axios.get("/profile/industry-experience", { headers });
      setIndustryExperiences(res.data || []);
    } catch (err) {}
  };

  const loadColleges = async () => {
    try {
      const res = await axios.get("/profile/colleges");
      setColleges(res.data || []);
    } catch (err) {}
  };

  const loadCompanies = async () => {
    try {
      const res = await axios.get("/profile/companies");
      setCompanies(res.data || []);
    } catch (err) {}
  };

  const savePersonalDetails = async () => {
    const requiredFieldLabels = {
      full_name: "Full Name",
      parent_name: "Parents Name / Husband Name",
      department: "Department",
      current_designation: "Current Designation",
      date_of_joining: "Date of Joining",
      date_of_birth: "Date of Birth",
      gender: "Gender",
      marital_status: "Marital Status",
      category: "Category",
      phone_number: "Phone Number",
      aadhar_card: "Aadhar Card",
      pan_card: "PAN Card",
      address: "Address",
    };

    const missingFieldEntry = Object.entries(requiredFieldLabels).find(
      ([key]) => !String(personalForm[key] || "").trim()
    );

    if (missingFieldEntry) {
      showNotification(`${missingFieldEntry[1]} is required`, "error");
      return;
    }

    if (!/^\d{10}$/.test(personalForm.phone_number)) {
      showNotification("Phone number must be exactly 10 digits", "error");
      return;
    }

    if (!/^\d{12}$/.test(personalForm.aadhar_card)) {
      showNotification("Aadhar card must be exactly 12 digits", "error");
      return;
    }

    try {
      await axios.put("/profile/personal-details", personalForm, { headers });
      showNotification("Personal details saved!");
    } catch (err) {
      showNotification("Failed to save personal details", "error");
    }
  };

  const addTeachingExperience = async () => {
    const collegeName = showOtherCollegeName ? otherCollegeName : teachingForm.college_name;
    
    if (!collegeName || !String(collegeName).trim()) {
      showNotification("College name is required", "error");
      return;
    }

    if (!teachingForm.designation || !String(teachingForm.designation).trim()) {
      showNotification("Designation is required", "error");
      return;
    }

    if (!teachingForm.status || !String(teachingForm.status).trim()) {
      showNotification("Status is required", "error");
      return;
    }

    if (!teachingForm.date_of_entry) {
      showNotification("Date of entry is required", "error");
      return;
    }

    if (!teachingForm.is_current_affiliated && !teachingForm.date_of_exit) {
      showNotification("Date of exit is required if not currently affiliated", "error");
      return;
    }

    try {
      // If adding new college, add it first
      if (showOtherCollegeName && otherCollegeName) {
        try {
          await axios.post("/profile/colleges", { college_name: otherCollegeName }, { headers });
          await loadColleges();
        } catch (err) {
          // College might already exist, continue anyway
          console.log("College addition note:", err.response?.data?.message);
        }
      }

      const payload = {
        college_name: collegeName.trim(),
        designation: teachingForm.designation.trim(),
        status: teachingForm.status.trim(),
        is_current_affiliated: teachingForm.is_current_affiliated === true,
        date_of_entry: teachingForm.date_of_entry,
        date_of_exit: teachingForm.is_current_affiliated ? null : teachingForm.date_of_exit
      };

      console.log("Sending teaching experience payload:", payload);

      if (editingTeachingId) {
        await axios.delete(`/profile/teaching-experience/${editingTeachingId}`, { headers });
      }

      await axios.post("/profile/teaching-experience", payload, { headers });
      showNotification(editingTeachingId ? "Teaching experience updated successfully!" : "Teaching experience added successfully!");
      loadTeachingExperience();
      setTeachingForm({ experience_type: "Teaching", college_name: "", designation: "", status: "", is_current_affiliated: false, date_of_entry: "", date_of_exit: "" });
      setShowOtherCollegeName(false);
      setOtherCollegeName("");
      setEditingTeachingId(null);
    } catch (err) {
      console.error("Teaching experience add error:", err.response?.data || err.message);
      const errorMsg = err.response?.data?.message || (editingTeachingId ? "Failed to update teaching experience" : "Failed to add teaching experience");
      showNotification(errorMsg, "error");
    }
  };

  const startEditTeachingExperience = (exp) => {
    setTeachingForm({
      experience_type: "Teaching",
      college_name: exp.college_name || "",
      designation: exp.designation || "",
      status: exp.status || "",
      is_current_affiliated: Boolean(exp.is_current_affiliated),
      date_of_entry: formatDateOnly(exp.date_of_entry),
      date_of_exit: formatDateOnly(exp.date_of_exit),
    });
    setEditingTeachingId(exp.id);
  };

  const addIndustryExperience = async () => {
    const companyName = showOtherCompanyName ? otherCompanyName : industrialForm.company_name;
    
    if (!companyName || !String(companyName).trim()) {
      showNotification("Company name is required", "error");
      return;
    }

    if (!industrialForm.designation || !String(industrialForm.designation).trim()) {
      showNotification("Designation is required", "error");
      return;
    }

    if (!industrialForm.date_of_entry) {
      showNotification("Date of entry is required", "error");
      return;
    }

    if (!industrialForm.date_of_exit) {
      showNotification("Date of exit is required", "error");
      return;
    }

    try {
      // If adding new company, add it first
      if (showOtherCompanyName && otherCompanyName) {
        try {
          await axios.post("/profile/companies", { company_name: otherCompanyName }, { headers });
          await loadCompanies();
        } catch (err) {
          // Company might already exist, continue anyway
          console.log("Company addition note:", err.response?.data?.message);
        }
      }

      const payload = {
        company_name: companyName.trim(),
        designation: industrialForm.designation.trim(),
        is_current_affiliated: false,
        date_of_entry: industrialForm.date_of_entry,
        date_of_exit: industrialForm.date_of_exit
      };

      console.log("Sending industry experience payload:", payload);

      if (editingIndustryId) {
        await axios.delete(`/profile/industry-experience/${editingIndustryId}`, { headers });
      }

      await axios.post("/profile/industry-experience", payload, { headers });
      showNotification(editingIndustryId ? "Industry experience updated successfully!" : "Industry experience added successfully!");
      loadIndustryExperience();
      setIndustrialForm({ company_name: "", designation: "", is_current_affiliated: false, date_of_entry: "", date_of_exit: "" });
      setShowOtherCompanyName(false);
      setOtherCompanyName("");
      setEditingIndustryId(null);
    } catch (err) {
      console.error("Industry experience add error:", err.response?.data || err.message);
      const errorMsg = err.response?.data?.message || (editingIndustryId ? "Failed to update industry experience" : "Failed to add industry experience");
      showNotification(errorMsg, "error");
    }
  };

  const startEditIndustryExperience = (exp) => {
    setIndustrialForm({
      company_name: exp.company_name || "",
      designation: exp.designation || "",
      is_current_affiliated: Boolean(exp.is_current_affiliated),
      date_of_entry: formatDateOnly(exp.date_of_entry),
      date_of_exit: formatDateOnly(exp.date_of_exit),
    });
    setEditingIndustryId(exp.id);
  };

  const deleteTeachingExp = async (id) => {
    try {
      await axios.delete(`/profile/teaching-experience/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadTeachingExperience();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const deleteIndustryExp = async (id) => {
    try {
      await axios.delete(`/profile/industry-experience/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadIndustryExperience();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const calculateYearsExperienceDisplay = (dateEntry, dateExit, isCurrentAffiliated) => {
    if (!dateEntry) return "";
    const start = new Date(dateEntry);
    const end = isCurrentAffiliated ? new Date() : (dateExit ? new Date(dateExit) : new Date());
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return "";
    
    const diffMs = end - start;
    const totalMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30.44));
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    
    if (years === 0) return `${months} months`;
    if (months === 0) return `${years} year${years > 1 ? 's' : ''}`;
    return `${years} year${years > 1 ? 's' : ''} ${months} months`;
  };

  return (
    <div className="space-y-8">
      {section === "personal" && (
      <>
      {/* Personal Details */}
      <Section title="Personal Details">
        <div className="mb-4">
          <VerificationStatusPill status={personalForm.verification_status} />
        </div>

        {/* Profile Photo */}
        <div className="mb-6 flex flex-col items-center sm:flex-row sm:items-start gap-4">
          <div className="flex-shrink-0">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-blue-200 shadow-md"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Profile Photo</label>
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors ${photoUploading ? "bg-gray-200 text-gray-500" : "bg-blue-100 text-blue-700 hover:bg-blue-200"}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {photoUploading ? "Uploading..." : "Upload Photo"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoUpload}
                disabled={photoUploading}
                className="hidden"
              />
            </label>
            <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP. Max 5MB.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Full Name" value={personalForm.full_name} required
            onChange={(e) => setPersonalForm({...personalForm, full_name: e.target.value})} />
          <Input label="Parents Name / Husband Name" value={personalForm.parent_name} required
            onChange={(e) => setPersonalForm({...personalForm, parent_name: e.target.value})} />
          <SelectInput
            label="Department"
            value={personalForm.department}
            options={DEPARTMENT_OPTIONS}
            required
            onChange={(e) => setPersonalForm({ ...personalForm, department: e.target.value })}
          />
          <SelectInput
            label="Current Designation"
            value={personalForm.current_designation}
            options={CURRENT_DESIGNATION_OPTIONS}
            required
            onChange={(e) => setPersonalForm({ ...personalForm, current_designation: e.target.value })}
          />
          <Input label="Date of Joining" type="date" value={personalForm.date_of_joining} required
            onChange={(e) => setPersonalForm({...personalForm, date_of_joining: e.target.value})} />
          <Input label="Date of Birth" type="date" value={personalForm.date_of_birth} required
            onChange={(e) => setPersonalForm({...personalForm, date_of_birth: e.target.value})} />
          <SelectInput
            label="Gender"
            value={personalForm.gender}
            options={GENDER_OPTIONS}
            required
            onChange={(e) => setPersonalForm({ ...personalForm, gender: e.target.value })}
          />
          <SelectInput
            label="Marital Status"
            value={personalForm.marital_status}
            options={MARITAL_STATUS_OPTIONS}
            required
            onChange={(e) => setPersonalForm({ ...personalForm, marital_status: e.target.value })}
          />
          <SelectInput
            label="Category"
            value={personalForm.category}
            options={CATEGORY_OPTIONS}
            required
            onChange={(e) => setPersonalForm({ ...personalForm, category: e.target.value })}
          />
          <Input label="Phone Number" value={personalForm.phone_number} required
            onChange={(e) => setPersonalForm({...personalForm, phone_number: e.target.value})} 
            placeholder="10 digit number" maxLength="10" />
          <Input label="Aadhar Card" value={personalForm.aadhar_card} required
            onChange={(e) => setPersonalForm({...personalForm, aadhar_card: e.target.value})} 
            placeholder="12 digit Aadhar number" maxLength="12" />
          <Input label="PAN Card" value={personalForm.pan_card} required
            onChange={(e) => setPersonalForm({...personalForm, pan_card: e.target.value})} 
            placeholder="PAN number" />
          <Input label="Address" value={personalForm.address} required
            onChange={(e) => setPersonalForm({...personalForm, address: e.target.value})} />
        </div>
        <button onClick={savePersonalDetails} className="btn-primary mt-4">Save Personal Details</button>
      </Section>
      </>
      )}

      {section === "experience" && (
      <>
      {/* Teaching Experience */}
      <Section title="Teaching Experience">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              College Name <span className="text-red-500">*</span>
            </label>
            {!showOtherCollegeName ? (
              <div className="flex gap-2">
                <select 
                  value={teachingForm.college_name}
                  onChange={(e) => {
                    if (e.target.value === "OTHER") {
                      setShowOtherCollegeName(true);
                      setTeachingForm({...teachingForm, college_name: ""});
                    } else {
                      setTeachingForm({...teachingForm, college_name: e.target.value});
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white text-sm"
                >
                  <option value="">Select College</option>
                  {colleges.map((c) => (
                    <option key={c.id ?? c.college_name} value={c.college_name}>{c.college_name}</option>
                  ))}
                  <option value="OTHER">Other (Type to add new)</option>
                </select>
                {teachingForm.college_name && (
                  <button type="button" onClick={() => setShowOtherCollegeName(false)} className="px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors bg-white whitespace-nowrap">Clear</button>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={otherCollegeName}
                  onChange={(e) => setOtherCollegeName(e.target.value)}
                  placeholder="Enter new college name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                />
                <button type="button" onClick={() => setShowOtherCollegeName(false)} className="px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors bg-white whitespace-nowrap">Use List</button>
              </div>
            )}
          </div>

          <SelectInput
            label="Designation"
            value={teachingForm.designation}
            options={TEACHING_DESIGNATION_OPTIONS}
            required
            onChange={(e) => setTeachingForm({...teachingForm, designation: e.target.value})}
          />

          <SelectInput
            label="Status"
            value={teachingForm.status}
            options={["University Approved", "Adhoc", "Management Regular"]}
            required
            onChange={(e) => setTeachingForm({...teachingForm, status: e.target.value})}
          />

          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input 
                type="checkbox"
                checked={teachingForm.is_current_affiliated}
                onChange={(e) => setTeachingForm({...teachingForm, is_current_affiliated: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700">Currently Affiliated</span>
            </label>
          </div>

          <Input label="Date of Entry" type="date" value={teachingForm.date_of_entry} required
            onChange={(e) => setTeachingForm({ ...teachingForm, date_of_entry: e.target.value })} />

          {!teachingForm.is_current_affiliated && (
            <Input label="Date of Exit" type="date" value={teachingForm.date_of_exit} required
              onChange={(e) => setTeachingForm({ ...teachingForm, date_of_exit: e.target.value })} />
          )}
        </div>
        <button onClick={addTeachingExperience} className="btn-primary">{editingTeachingId ? "Update Teaching Experience" : "Add Teaching Experience"}</button>

        {teachingExperiences.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Teaching Experience List</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">College Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Designation</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Status</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Date of Entry</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Date of Exit</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Experience</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Verification</th>
                    <th className="border border-gray-300 px-4 py-2 text-center text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByYearDesc(teachingExperiences).map((exp) => (
                    <tr key={exp.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.college_name}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.designation}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.status}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{formatDate(exp.date_of_entry)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.is_current_affiliated ? "Present" : formatDate(exp.date_of_exit)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{calculateYearsExperienceDisplay(exp.date_of_entry, exp.date_of_exit, exp.is_current_affiliated)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        <span className={exp.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                          {exp.verification_status}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <div className="flex justify-center gap-2">
                          <ActionIconButton label="Edit teaching experience" variant="edit" onClick={() => startEditTeachingExperience(exp)} />
                          <ActionIconButton label="Delete teaching experience" variant="delete" onClick={() => deleteTeachingExp(exp.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      {/* Industry Experience */}
      <Section title="Industry Experience">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Company Name <span className="text-red-500">*</span>
            </label>
            {!showOtherCompanyName ? (
              <div className="flex gap-2">
                <select 
                  value={industrialForm.company_name}
                  onChange={(e) => {
                    if (e.target.value === "OTHER") {
                      setShowOtherCompanyName(true);
                      setIndustrialForm({...industrialForm, company_name: ""});
                    } else {
                      setIndustrialForm({...industrialForm, company_name: e.target.value});
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white text-sm"
                >
                  <option value="">Select Company</option>
                  {companies.map((c) => (
                    <option key={c.id ?? c.company_name} value={c.company_name}>{c.company_name}</option>
                  ))}
                  <option value="OTHER">Other (Type to add new)</option>
                </select>
                {industrialForm.company_name && (
                  <button type="button" onClick={() => setShowOtherCompanyName(false)} className="px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors bg-white whitespace-nowrap">Clear</button>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={otherCompanyName}
                  onChange={(e) => setOtherCompanyName(e.target.value)}
                  placeholder="Enter new company name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                />
                <button type="button" onClick={() => setShowOtherCompanyName(false)} className="px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors bg-white whitespace-nowrap">Use List</button>
              </div>
            )}
          </div>

          <Input label="Designation" value={industrialForm.designation} required
            onChange={(e) => setIndustrialForm({...industrialForm, designation: e.target.value})} 
            placeholder="e.g., Software Engineer, Manager, etc." />

          <Input label="Date of Entry" type="date" value={industrialForm.date_of_entry} required
            onChange={(e) => setIndustrialForm({ ...industrialForm, date_of_entry: e.target.value })} />

          <Input label="Date of Exit" type="date" value={industrialForm.date_of_exit} required
            onChange={(e) => setIndustrialForm({ ...industrialForm, date_of_exit: e.target.value })} />
        </div>
        <button onClick={addIndustryExperience} className="btn-primary">{editingIndustryId ? "Update Industry Experience" : "Add Industry Experience"}</button>

        {industryExperiences.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Industry Experience List</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Company Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Designation</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Date of Entry</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Date of Exit</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Experience</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Status</th>
                    <th className="border border-gray-300 px-4 py-2 text-center text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByYearDesc(industryExperiences).map((exp) => (
                    <tr key={exp.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.company_name}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.designation}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{formatDate(exp.date_of_entry)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{exp.is_current_affiliated ? "Present" : formatDate(exp.date_of_exit)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">{calculateYearsExperienceDisplay(exp.date_of_entry, exp.date_of_exit, exp.is_current_affiliated)}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        <span className={exp.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                          {exp.verification_status}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <div className="flex justify-center gap-2">
                          <ActionIconButton label="Edit industry experience" variant="edit" onClick={() => startEditIndustryExperience(exp)} />
                          <ActionIconButton label="Delete industry experience" variant="delete" onClick={() => deleteIndustryExp(exp.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>
      </>
      )}
    </div>
  );
}
function EducationTab({ headers, showNotification }) {
  const [educationForm, setEducationForm] = useState({
    qualification_type: "", qualification_other: "", university: "", year_of_passing: "", percentage: "", division: "", specialization: "", is_pursuing: false
  });
  const [editingEducationId, setEditingEducationId] = useState(null);
  const [educationList, setEducationList] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [showOtherUniversity, setShowOtherUniversity] = useState(false);
  const [otherUniversityName, setOtherUniversityName] = useState("");

  useEffect(() => {
    loadEducation();
    loadUniversities();
  }, []);

  const loadUniversities = async () => {
    try {
      const res = await axios.get("/profile/universities");
      setUniversities(res.data || []);
    } catch (err) {}
  };

  const passingYearOptions = Array.from(
    { length: new Date().getFullYear() - 1979 },
    (_, index) => String(new Date().getFullYear() - index)
  );

  const loadEducation = async () => {
    try {
      const res = await axios.get("/profile/education", { headers });
      setEducationList(res.data || []);
    } catch (err) {}
  };

  const resetEducationForm = () => {
    setEditingEducationId(null);
    setEducationForm({
      qualification_type: "", qualification_other: "", university: "", year_of_passing: "",
      percentage: "", division: "", specialization: "", is_pursuing: false
    });
  };

  const startEditEducation = (edu) => {
    setEditingEducationId(edu.id);
    setEducationForm({
      qualification_type: edu.qualification_type || "",
      qualification_other: edu.qualification_other || "",
      university: edu.university || "",
      year_of_passing: edu.year_of_passing ? String(edu.year_of_passing) : "",
      percentage: edu.percentage || "",
      division: edu.division || "",
      specialization: edu.specialization || "",
      is_pursuing: Boolean(edu.is_pursuing),
    });
  };

  const saveEducation = async () => {
    try {
      if (editingEducationId) {
        await axios.put(`/profile/education/${editingEducationId}`, educationForm, { headers });
        showNotification("Education updated!");
      } else {
        await axios.post("/profile/education", educationForm, { headers });
        showNotification("Education added!");
      }
      loadEducation();
      resetEducationForm();
    } catch (err) {
      showNotification(editingEducationId ? "Failed to update education" : "Failed to add education", "error");
    }
  };

  const cancelEducationEdit = () => {
    resetEducationForm();
  };

  const deleteEducation = async (id) => {
    try {
      await axios.delete(`/profile/education/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadEducation();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  return (
    <div className="space-y-8">
      <Section title="Educational Qualifications">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Qualification Type"
            value={educationForm.qualification_type}
            required
            options={["PhD", "MTech", "ME", "BE", "BTech", "MSc", "BSc", "B.Ed", "B.Com", "Diploma", "MA", "BCA", "MCA", "MBA", "BBA", "Other"]}
            onChange={(e) => setEducationForm({ ...educationForm, qualification_type: e.target.value })}
          />
          {educationForm.qualification_type === "Other" && (
            <Input
              label="Qualification Other"
              value={educationForm.qualification_other}
              onChange={(e) => setEducationForm({ ...educationForm, qualification_other: e.target.value })}
            />
          )}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              University<span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={showOtherUniversity ? "__other__" : educationForm.university}
              onChange={(e) => {
                if (e.target.value === "__other__") {
                  setShowOtherUniversity(true);
                  setEducationForm({ ...educationForm, university: "" });
                } else {
                  setShowOtherUniversity(false);
                  setOtherUniversityName("");
                  setEducationForm({ ...educationForm, university: e.target.value });
                }
              }}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
            >
              <option value="">Select University</option>
              {universities.map((u) => (
                <option key={u.university_name} value={u.university_name}>{u.university_name}</option>
              ))}
              <option value="__other__">Other (Type manually)</option>
            </select>
          </div>
          {showOtherUniversity && (
            <Input label="University Name" value={otherUniversityName} required
              onChange={(e) => {
                setOtherUniversityName(e.target.value);
                setEducationForm({ ...educationForm, university: e.target.value });
              }}
              placeholder="Type university name"
            />
          )}
          <SelectInput
            label="Year of Passing"
            value={educationForm.year_of_passing}
            required
            options={passingYearOptions}
            onChange={(e) => setEducationForm({ ...educationForm, year_of_passing: e.target.value })}
          />
          <Input label="Percentage" value={educationForm.percentage}
            onChange={(e) => setEducationForm({...educationForm, percentage: e.target.value})} />
          <Input label="Division" value={educationForm.division}
            onChange={(e) => setEducationForm({...educationForm, division: e.target.value})} />
          <Input label="Specialization" value={educationForm.specialization}
            onChange={(e) => setEducationForm({...educationForm, specialization: e.target.value})} />
        </div>
        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="is_pursuing"
            checked={educationForm.is_pursuing}
            onChange={(e) => setEducationForm({...educationForm, is_pursuing: e.target.checked})}
            className="w-4 h-4 cursor-pointer"
          />
          <label htmlFor="is_pursuing" className="cursor-pointer font-semibold text-gray-700">
            Currently Pursuing this Qualification
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={saveEducation} className="btn-primary">
            {editingEducationId ? "Update Education" : "Add Education"}
          </button>
          {editingEducationId && (
            <button onClick={cancelEducationEdit} className="btn-secondary">
              Cancel
            </button>
          )}
        </div>

        {/* Display saved education in table format */}
        {educationList.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-700 text-lg mb-4">Saved Qualifications</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Qualification</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">University</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Year</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Specialization</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Percentage</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                     <th className="px-4 py-3 text-left font-semibold text-gray-700">Pursuing</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByYearDesc(educationList).map((edu) => (
                    <tr key={edu.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">
                        <div className="font-semibold">{edu.qualification_type}</div>
                        {edu.qualification_other && <div className="text-xs text-blue-600">{edu.qualification_other}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{edu.university}</td>
                      <td className="px-4 py-3 text-gray-700">{edu.year_of_passing}</td>
                      <td className="px-4 py-3 text-gray-700">{edu.specialization || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{edu.percentage ? `${edu.percentage}%` : "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          edu.verification_status === "verified" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {edu.verification_status}
                        </span>
                      </td>
                       <td className="px-4 py-3 text-center">
                         {edu.is_pursuing ? (
                           <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                             ✓ Yes
                           </span>
                         ) : (
                           <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                             -
                           </span>
                         )}
                       </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <ActionIconButton label="Edit education" variant="edit" onClick={() => startEditEducation(edu)} />
                          <ActionIconButton label="Delete education" variant="delete" onClick={() => deleteEducation(edu.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

/* ===============================================
   ACADEMIC INFO TAB
=============================================== */
function AcademicInfo({ headers, showNotification }) {
  const [subjectForm, setSubjectForm] = useState({ 
    program_name: "", semester: "", academic_year: "", subject_name: "", subject_category: "" 
  });
  const [innovationForm, setInnovationForm] = useState({ 
    level: "", project_name: "", project_year: "", academic_year: "", role: "", rank_awarded: "" 
  });
  const [setupForm, setSetupForm] = useState({ 
    setup_name: "", laboratory_name: "", expenses: "", semester: "", academic_year: "" 
  });
  const [editingSetupId, setEditingSetupId] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({ 
    subject_name: "", semester: "", academic_year: "", feedback_score: "" 
  });
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [innovations, setInnovations] = useState([]);
  const [setups, setSetups] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [innovationsLoading, setInnovationsLoading] = useState(true);
  const [setupsLoading, setSetupsLoading] = useState(true);
  const [feedbacksLoading, setFeedbacksLoading] = useState(true);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editingInnovationId, setEditingInnovationId] = useState(null);

  useEffect(() => {
    loadSubjects();
    loadInnovations();
    loadSetups();
    loadFeedbacks();
  }, []);

  const loadSubjects = async () => {
    setSubjectsLoading(true);
    try {
      const res = await axios.get("/profile/subjects", { headers });
      setSubjects(res.data || []);
    } catch (err) {} finally {
      setSubjectsLoading(false);
    }
  };

  const loadInnovations = async () => {
    setInnovationsLoading(true);
    try {
      const res = await axios.get("/profile/innovation-projects", { headers });
      setInnovations(res.data || []);
    } catch (err) {} finally {
      setInnovationsLoading(false);
    }
  };

  const loadSetups = async () => {
    setSetupsLoading(true);
    try {
      const res = await axios.get("/profile/experimental-setups", { headers });
      setSetups(res.data || []);
    } catch (err) {} finally {
      setSetupsLoading(false);
    }
  };

  const loadFeedbacks = async () => {
    setFeedbacksLoading(true);
    try {
      const res = await axios.get("/profile/student-feedback", { headers });
      setFeedbacks(res.data || []);
    } catch (err) {} finally {
      setFeedbacksLoading(false);
    }
  };

  const startEditSubject = (subject) => {
    setEditingSubjectId(subject.id);
    setSubjectForm({
      program_name: subject.program_name || "",
      semester: subject.semester || "",
      academic_year: subject.academic_year || "",
      subject_name: subject.subject_name || "",
      subject_category: subject.subject_category || "",
    });
  };

  const startEditInnovation = (innovation) => {
    setEditingInnovationId(innovation.id);
    setInnovationForm({
      level: innovation.level || "",
      project_name: innovation.project_name || "",
      project_year: innovation.project_year ? String(innovation.project_year) : "",
      academic_year: innovation.academic_year || "",
      role: innovation.role || innovation.mentor_role || "",
      rank_awarded: innovation.rank_awarded || "",
    });
  };

  const addSubject = async () => {
    try {
      if (editingSubjectId) {
        await axios.delete(`/profile/subjects/${editingSubjectId}`, { headers });
      }
      await axios.post("/profile/subjects", subjectForm, { headers });
      showNotification(editingSubjectId ? "Subject updated!" : "Subject added!");
      loadSubjects();
      setSubjectForm({ program_name: "", semester: "", academic_year: "", subject_name: "", subject_category: "" });
      setEditingSubjectId(null);
    } catch (err) {
      showNotification(editingSubjectId ? "Failed to update subject" : "Failed to add subject", "error");
    }
  };

  const addInnovation = async () => {
    try {
      if (!["1", "2", "3", "Participation Only"].includes(String(innovationForm.rank_awarded))) {
        showNotification("Rank must be 1, 2, 3, or Participation Only", "error");
        return;
      }
      if (editingInnovationId) {
        await axios.delete(`/profile/innovation-projects/${editingInnovationId}`, { headers });
      }
      await axios.post("/profile/innovation-projects", innovationForm, { headers });
      showNotification(editingInnovationId ? "Innovation project updated!" : "Innovation project added!");
      loadInnovations();
      setInnovationForm({ level: "", project_name: "", project_year: "", academic_year: "", role: "", rank_awarded: "" });
      setEditingInnovationId(null);
    } catch (err) {
      showNotification(editingInnovationId ? "Failed to update innovation project" : "Failed to add innovation project", "error");
    }
  };

  const deleteSubject = async (id) => {
    try {
      await axios.delete(`/profile/subjects/${id}`, { headers });
      showNotification("Subject deleted successfully!");
      loadSubjects();
    } catch (err) {
      showNotification("Failed to delete subject", "error");
    }
  };

  const deleteInnovation = async (id) => {
    try {
      await axios.delete(`/profile/innovation-projects/${id}`, { headers });
      showNotification("Innovation project deleted successfully!");
      loadInnovations();
    } catch (err) {
      showNotification("Failed to delete innovation project", "error");
    }
  };

  const addSetup = async () => {
    try {
      if (editingSetupId) {
        await axios.delete(`/profile/experimental-setups/${editingSetupId}`, { headers });
      }
      await axios.post("/profile/experimental-setups", setupForm, { headers });
      showNotification(editingSetupId ? "Experimental setup updated!" : "Experimental setup added!");
      loadSetups();
      setSetupForm({ setup_name: "", laboratory_name: "", expenses: "", semester: "", academic_year: "" });
      setEditingSetupId(null);
    } catch (err) {
      showNotification(editingSetupId ? "Failed to update setup" : "Failed to add setup", "error");
    }
  };

  const startEditSetup = (setup) => {
    setSetupForm({
      setup_name: setup.setup_name || "",
      laboratory_name: setup.laboratory_name || "",
      expenses: setup.expenses || "",
      semester: setup.semester || "",
      academic_year: setup.academic_year || "",
    });
    setEditingSetupId(setup.id);
  };

  const deleteSetup = async (id) => {
    try {
      await axios.delete(`/profile/experimental-setups/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadSetups();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addFeedback = async () => {
    try {
      if (editingFeedbackId) {
        await axios.delete(`/profile/student-feedback/${editingFeedbackId}`, { headers });
      }
      await axios.post("/profile/student-feedback", feedbackForm, { headers });
      showNotification(editingFeedbackId ? "Feedback updated!" : "Feedback added!");
      loadFeedbacks();
      setFeedbackForm({ subject_name: "", semester: "", academic_year: "", feedback_score: "" });
      setEditingFeedbackId(null);
    } catch (err) {
      showNotification(editingFeedbackId ? "Failed to update feedback" : "Failed to add feedback", "error");
    }
  };

  const startEditFeedback = (feedback) => {
    setFeedbackForm({
      subject_name: feedback.subject_name || "",
      semester: feedback.semester || "",
      academic_year: feedback.academic_year || "",
      feedback_score: feedback.feedback_score || "",
    });
    setEditingFeedbackId(feedback.id);
  };

  const deleteFeedback = async (id) => {
    try {
      await axios.delete(`/profile/student-feedback/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadFeedbacks();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  return (
    <div className="space-y-8">
      {/* Subjects Taught */}
      <Section title="Subjects Taught">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Program Name"
            value={subjectForm.program_name}
            options={PROGRAM_OPTIONS}
            onChange={(e) => setSubjectForm({ ...subjectForm, program_name: e.target.value })}
          />
          <SelectInput
            label="Semester"
            value={subjectForm.semester}
            options={SEMESTER_OPTIONS}
            onChange={(e) => setSubjectForm({ ...subjectForm, semester: e.target.value })}
          />
          <SelectInput
            label="Academic Year"
            value={subjectForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setSubjectForm({ ...subjectForm, academic_year: e.target.value })}
          />
          <Input label="Subject Name" value={subjectForm.subject_name}
            onChange={(e) => setSubjectForm({...subjectForm, subject_name: e.target.value})} />
          <SelectInput
            label="Subject Category"
            value={subjectForm.subject_category}
            options={["Theory", "Practical", "Tutorial", "Lab", "Elective"]}
            onChange={(e) => setSubjectForm({ ...subjectForm, subject_category: e.target.value })}
          />
        </div>
        <button onClick={addSubject} className="btn-primary mt-4">
          {editingSubjectId ? "Update Subject" : "Add Subject"}
        </button>

        {subjectsLoading ? (
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">Loading subjects...</div>
        ) : subjects.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-700 text-lg mb-4">Saved Subjects</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-xs md:text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Subject</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Program</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Sem</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Category</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByYearDesc(subjects).map((subject) => (
                    <tr key={subject.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900 font-semibold">{subject.subject_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{subject.program_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{subject.semester || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{subject.academic_year || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{subject.subject_category || "-"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          subject.verification_status === "verified"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {subject.verification_status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <ActionIconButton label="Edit subject" variant="edit" onClick={() => startEditSubject(subject)} />
                          <ActionIconButton label="Delete subject" variant="delete" onClick={() => deleteSubject(subject.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      {/* Innovation Projects (renamed per request) */}
      <Section title="Support in Students Innovation projects">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Level"
            value={innovationForm.level}
            options={["Department", "Institute", "State", "National", "International"]}
            onChange={(e) => setInnovationForm({ ...innovationForm, level: e.target.value })}
          />
          <Input label="Project Name" value={innovationForm.project_name}
            onChange={(e) => setInnovationForm({...innovationForm, project_name: e.target.value})} />
          <SelectInput
            label="Year"
            value={innovationForm.project_year}
            options={YEAR_OPTIONS}
            onChange={(e) => setInnovationForm({ ...innovationForm, project_year: e.target.value })}
          />
          <SelectInput
            label="Academic Year"
            value={innovationForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setInnovationForm({ ...innovationForm, academic_year: e.target.value })}
          />
          <SelectInput
            label="Role"
            value={innovationForm.role}
            options={["mentor", "co - mentor", "supporting staff"]}
            onChange={(e) => setInnovationForm({ ...innovationForm, role: e.target.value })}
          />
          <SelectInput
            label="Rank Awarded / Participation"
            value={innovationForm.rank_awarded}
            options={["1", "2", "3", "Participation Only"]}
            onChange={(e) => setInnovationForm({ ...innovationForm, rank_awarded: e.target.value })}
          />
        </div>
        <button onClick={addInnovation} className="btn-primary mt-4">
          {editingInnovationId ? "Update Innovation Project" : "Add Innovation Project"}
        </button>

        {innovationsLoading ? (
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">Loading innovation projects...</div>
        ) : innovations.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-700 text-lg mb-4">Saved Innovations</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-xs md:text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Project</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Level</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Year</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Role</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Rank</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByYearDesc(innovations).map((innovation) => (
                    <tr key={innovation.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900 font-semibold">{innovation.project_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{innovation.level || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{innovation.project_year || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{innovation.academic_year || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{innovation.role || innovation.mentor_role || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{innovation.rank_awarded || "-"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          innovation.verification_status === "verified"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {innovation.verification_status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <ActionIconButton label="Edit innovation project" variant="edit" onClick={() => startEditInnovation(innovation)} />
                          <ActionIconButton label="Delete innovation project" variant="delete" onClick={() => deleteInnovation(innovation.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      {/* Experimental Setups */}
      <Section title="Experimental Setups">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Setup Name" value={setupForm.setup_name}
            onChange={(e) => setSetupForm({...setupForm, setup_name: e.target.value})} />
          <Input label="Laboratory Name" value={setupForm.laboratory_name}
            onChange={(e) => setSetupForm({...setupForm, laboratory_name: e.target.value})} />
          <Input label="Expenses" value={setupForm.expenses}
            type="number"
            onChange={(e) => setSetupForm({...setupForm, expenses: e.target.value})} />
          <SelectInput
            label="Semester"
            value={setupForm.semester}
            options={SEMESTER_OPTIONS}
            onChange={(e) => setSetupForm({ ...setupForm, semester: e.target.value })}
          />
          <SelectInput
            label="Academic Year"
            value={setupForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setSetupForm({ ...setupForm, academic_year: e.target.value })}
          />
        </div>
        <button onClick={addSetup} className="btn-primary">{editingSetupId ? "Update Setup" : "Add Setup"}</button>

        {setups.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-700 text-lg mb-4">Saved Setups</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-xs md:text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Setup</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Laboratory</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Sem</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Expenses</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByYearDesc(setups).map((setup) => (
                    <tr key={setup.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900 font-semibold">{setup.setup_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{setup.laboratory_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{setup.semester || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{setup.academic_year || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{setup.expenses ? `₹${setup.expenses}` : "-"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          setup.verification_status === "verified"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {setup.verification_status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-2">
                          <ActionIconButton label="Edit setup" variant="edit" onClick={() => startEditSetup(setup)} />
                          <ActionIconButton label="Delete setup" variant="delete" onClick={() => deleteSetup(setup.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      {/* Student Feedback */}
      <Section title="Student Feedback">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Input label="Subject Name" value={feedbackForm.subject_name}
            onChange={(e) => setFeedbackForm({...feedbackForm, subject_name: e.target.value})} />
          <SelectInput
            label="Semester"
            value={feedbackForm.semester}
            options={SEMESTER_OPTIONS}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, semester: e.target.value })}
          />
          <SelectInput
            label="Academic Year"
            value={feedbackForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, academic_year: e.target.value })}
          />
          <Input label="Feedback Score" value={feedbackForm.feedback_score}
            onChange={(e) => setFeedbackForm({...feedbackForm, feedback_score: e.target.value})} />
        </div>
        <button onClick={addFeedback} className="btn-primary">{editingFeedbackId ? "Update Feedback" : "Add Feedback"}</button>

        {feedbacks.length > 0 && (
          <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Subject</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Semester</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Score</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(feedbacks).map((fb) => (
                  <tr key={fb.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-semibold">{fb.subject_name || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fb.semester || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fb.academic_year || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fb.feedback_score ? `${fb.feedback_score}/5` : "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        fb.verification_status === "verified"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {fb.verification_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit feedback" variant="edit" onClick={() => startEditFeedback(fb)} />
                        <ActionIconButton label="Delete feedback" variant="delete" onClick={() => deleteFeedback(fb.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

/* ===============================================
   RESEARCH INFO TAB (WITH INDEXING OPTIONS)
=============================================== */
function ResearchInfo({ headers, showNotification }) {
  const [googleScholarId, setGoogleScholarId] = useState("");
  const [scopusId, setScopusId] = useState("");
  const [wosId, setWosId] = useState("");
  const [orcidId, setOrcidId] = useState("");
  const [identifiersLoading, setIdentifiersLoading] = useState(false);

  const loadResearchIdentifiers = async () => {
    try {
      const res = await axios.get("/profile/personal-details", { headers });
      setGoogleScholarId(res.data.google_scholar_id || "");
      setScopusId(res.data.scopus_id || "");
      setWosId(res.data.wos_id || "");
      setOrcidId(res.data.orcid_id || "");
    } catch (err) {
      console.error("Failed to load research identifiers:", err);
    }
  };

  const saveResearchIdentifiers = async () => {
    setIdentifiersLoading(true);
    try {
      await axios.put(
        "/profile/research-ids",
        {
          google_scholar_id: googleScholarId.trim(),
          scopus_id: scopusId.trim(),
          wos_id: wosId.trim(),
          orcid_id: orcidId.trim(),
        },
        { headers }
      );
      showNotification("Research Identifiers saved successfully!", "success");
    } catch (err) {
      console.error("Failed to save research identifiers:", err);
      showNotification(err.response?.data?.message || "Failed to save research identifiers", "error");
    } finally {
      setIdentifiersLoading(false);
    }
  };

  const [doi, setDoi] = useState("");
  const [researchData, setResearchData] = useState(null);
  const [researchMode, setResearchMode] = useState("doi");
  const [researchForm, setResearchForm] = useState({
    title: "",
    authors: "",
    journal_name: "",
    publication_year: "",
    academic_year: "",
    svpcet_affiliation: "",
    doi: "",
    publisher: "",
    type: "",
    category: "other",
    issn: "",
    url: "",
  });
  const [publicationIndexing, setPublicationIndexing] = useState("Other"); // WOS, Scopus, SCI/SCIE, Scopus + WOS, Other
  const [publicationMultiIndexing, setPublicationMultiIndexing] = useState([]);
  const [researchList, setResearchList] = useState([]);
  const [editingResearchId, setEditingResearchId] = useState(null);
  const [patentForm, setPatentForm] = useState({ 
    patent_name: "", filing_no: "", application_no: "", date_of_filing: "", date_published: "", grant_no: "", grant_date: "", patent_type: "", patent_status: "", academic_year: "", document_link: "" 
  });
  const [editingPatentId, setEditingPatentId] = useState(null);
  const [copyrightForm, setCopyrightForm] = useState({
    copyright_name: "",
    status: "",
    date_of_registration: "",
    academic_year: "",
    document_link: "",
  });
  const [editingCopyrightId, setEditingCopyrightId] = useState(null);
  const [industrialDesignForm, setIndustrialDesignForm] = useState({
    design_name: "",
    design_number: "",
    date_of_grant: "",
    academic_year: "",
  });
  const [editingIndustrialDesignId, setEditingIndustrialDesignId] = useState(null);
  const [journalForm, setJournalForm] = useState({ 
    role_type: "", journal_name: "", level: "", academic_year: "", letter_details: "", publisher: "" 
  });
  const [editingJournalId, setEditingJournalId] = useState(null);
  const [fundingForm, setFundingForm] = useState({ 
    agency_name: "", agency_type: "", amount: "", year: "", academic_year: "", letter_details: "" 
  });
  const [editingFundingId, setEditingFundingId] = useState(null);
  const [consultancyForm, setConsultancyForm] = useState({ 
    industry_name: "", consultancy_type: "", amount: "", year: "", academic_year: "", consultancy_date: "", letter_details: "" 
  });
  const [editingConsultancyId, setEditingConsultancyId] = useState(null);
  const [phdForm, setPhdForm] = useState({ 
    scholar_name: "", status: "", starting_year: "", ending_year: "", is_pursuing: false, academic_year: "", notification_details: "", thesis_title: "", document_link: "" 
  });
  const [editingPhdId, setEditingPhdId] = useState(null);

  const [sessionSpeakerForm, setSessionSpeakerForm] = useState({ role: "", name_of_conference: "", institute_name: "", institute_nirf_ranking: "", mode: "Online", level: "", letter_no: "", date: "", academic_year: "", document_link: "" });
  const [sessionSpeakerList, setSessionSpeakerList] = useState([]);
  const [editingSessionSpeakerId, setEditingSessionSpeakerId] = useState(null);

  const [patents, setPatents] = useState([]);
  const [copyrights, setCopyrights] = useState([]);
  const [industrialDesigns, setIndustrialDesigns] = useState([]);
  const [journals, setJournals] = useState([]);
  const [funding, setFunding] = useState([]);
  const [consultancy, setConsultancy] = useState([]);
  const [phds, setPhds] = useState([]);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    loadResearchIdentifiers();
    loadResearch();
    loadPatents();
    loadCopyrights();
    loadIndustrialDesigns();
    loadJournals();
    loadFunding();
    loadConsultancy();
    loadPhds();
    loadSessionSpeakers();
  };

  const loadResearch = async () => {
    try {
      const res = await axios.get("/profile/research", { headers });
      setResearchList(res.data || []);
    } catch (err) {}
  };

  const loadPatents = async () => {
    try {
      const res = await axios.get("/profile/patents", { headers });
      setPatents(res.data || []);
    } catch (err) {}
  };

  const loadCopyrights = async () => {
    try {
      const res = await axios.get("/profile/copyrights", { headers });
      setCopyrights(res.data || []);
    } catch (err) {}
  };

  const loadIndustrialDesigns = async () => {
    try {
      const res = await axios.get("/profile/industrial-designs", { headers });
      setIndustrialDesigns(res.data || []);
    } catch (err) {}
  };

  const loadJournals = async () => {
    try {
      const res = await axios.get("/profile/journal-roles", { headers });
      setJournals(res.data || []);
    } catch (err) {}
  };

  const loadFunding = async () => {
    try {
      const res = await axios.get("/profile/external-funding", { headers });
      setFunding(res.data || []);
    } catch (err) {}
  };

  const loadConsultancy = async () => {
    try {
      const res = await axios.get("/profile/consultancy", { headers });
      setConsultancy(res.data || []);
    } catch (err) {}
  };

  const loadPhds = async () => {
    try {
      const res = await axios.get("/profile/phd-scholars", { headers });
      setPhds(res.data || []);
    } catch (err) {}
  };

  const loadSessionSpeakers = async () => {
    try {
      const res = await axios.get("/profile/session-speaker-details", { headers });
      setSessionSpeakerList(res.data || []);
    } catch (err) {}
  };

  const fetchDOI = async () => {
    if (!doi.trim()) {
      return showNotification("Please enter a DOI", "error");
    }
    try {
      const res = await axios.post("/profile/fetch-doi", { doi: doi.trim() }, { headers });
      setResearchData(res.data.data);
      setResearchForm({
        title: res.data.data.title || "",
        authors: res.data.data.authors || "",
        journal_name: res.data.data.journal || "",
        publication_year: res.data.data.year ? String(res.data.data.year) : "",
        academic_year: "",
        doi: res.data.data.doi || doi.trim(),
        publisher: res.data.data.publisher || "",
        type: res.data.data.type || "",
        category: res.data.data.category || "other",
        issn: res.data.data.issn || "",
        url: res.data.data.url || "",
      });
      setPublicationIndexing("Other"); // Reset to default
      setPublicationMultiIndexing([]);
      showNotification("DOI fetched successfully!");
    } catch (err) {
      showNotification(err.response?.data?.message || "DOI fetch failed", "error");
    }
  };

  const addResearch = async () => {
    try {
      const payload = researchMode === "manual"
        ? researchForm
        : {
            ...researchForm,
            title: researchForm.title || researchData?.title,
            authors: researchForm.authors || researchData?.authors,
            journal_name: researchForm.journal_name || researchData?.journal,
            publication_year: researchForm.publication_year || (researchData?.year ? String(researchData.year) : ""),
            doi: researchForm.doi || researchData?.doi,
            publisher: researchForm.publisher || researchData?.publisher,
            type: researchForm.type || researchData?.type,
            category: researchForm.category || researchData?.category,
            issn: researchForm.issn || `Indexed: ${publicationIndexing}`,
            url: researchForm.url || researchData?.url,
          };

      if (!payload.title) return showNotification("Title is required", "error");
      if (!payload.svpcet_affiliation) return showNotification("SVPCET Affiliation is required (Yes/No)", "error");

      if (editingResearchId) {
        await axios.delete(`/profile/research/${editingResearchId}`, { headers });
      }

      await axios.post("/profile/research", {
        ...payload,
        indexing: publicationIndexing,
        indexing_tags: publicationMultiIndexing,
        academic_year: payload.academic_year || null,
      }, { headers });
      showNotification("Research publication added!");
      loadResearch();
      setResearchData(null);
      setDoi("");
      setResearchForm({
        title: "",
        authors: "",
        journal_name: "",
        publication_year: "",
        academic_year: "",
        svpcet_affiliation: "",
        doi: "",
        publisher: "",
        type: "",
        category: "other",
        issn: "",
        url: "",
      });
      setPublicationIndexing("Other");
      setPublicationMultiIndexing([]);
      setEditingResearchId(null);
    } catch (err) {
      showNotification(err.response?.data?.message || "Failed to add research", "error");
    }
  };

  const startEditResearch = (research) => {
    setResearchMode("manual");
    setResearchForm({
      title: research.title || "",
      authors: research.authors || "",
      journal_name: research.journal_name || "",
      publication_year: research.publication_year ? String(research.publication_year) : "",
      academic_year: research.academic_year || "",
      svpcet_affiliation: research.svpcet_affiliation || "",
      doi: research.doi || "",
      publisher: research.publisher || "",
      type: research.type || "",
      category: research.category || "other",
      issn: research.issn || "",
      url: research.url || "",
    });
    setPublicationIndexing((research.issn || "Other").replace(/^Indexed:\s*/i, "") || "Other");
    setPublicationMultiIndexing(
      String(research.indexing_tags || "")
        .split(/[|,]/)
        .map((tag) => tag.trim())
        .filter(Boolean)
    );
    setEditingResearchId(research.id);
  };

  const isFetchedResearch = researchMode === "doi" && Boolean(researchData);

  const deleteResearch = async (id) => {
    try {
      await axios.delete(`/profile/research/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadResearch();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addPatent = async () => {
    try {
      if (!patentForm.patent_type) {
        showNotification("Patent type is required", "error");
        return;
      }

      if (editingPatentId) {
        await axios.delete(`/profile/patents/${editingPatentId}`, { headers });
      }

      const cleanForm = { ...patentForm };
      if (patentForm.patent_status === "Filed") {
        cleanForm.application_no = null;
        cleanForm.date_published = null;
        cleanForm.grant_no = null;
        cleanForm.grant_date = null;
      } else if (patentForm.patent_status === "Published") {
        cleanForm.filing_no = null;
        cleanForm.date_of_filing = null;
        cleanForm.grant_no = null;
        cleanForm.grant_date = null;
      } else if (patentForm.patent_status === "Granted") {
        cleanForm.filing_no = null;
        cleanForm.date_of_filing = null;
        cleanForm.application_no = null;
        cleanForm.date_published = null;
      }

      await axios.post("/profile/patents", cleanForm, { headers });

      showNotification(editingPatentId ? "Patent updated!" : "Patent added!");
      loadPatents();
      setPatentForm({ patent_name: "", filing_no: "", application_no: "", date_of_filing: "", date_published: "", grant_no: "", grant_date: "", patent_type: "", patent_status: "", academic_year: "", document_link: "" });
      setEditingPatentId(null);
    } catch (err) {
      showNotification("Failed to save patent", "error");
    }
  };

  const startEditPatent = (patent) => {
    setPatentForm({
      patent_name: patent.patent_name || "",
      filing_no: patent.filing_no || "",
      application_no: patent.application_no || "",
      date_of_filing: formatDateOnly(patent.date_of_filing),
      date_published: formatDateOnly(patent.date_published),
      grant_no: patent.grant_no || "",
      grant_date: formatDateOnly(patent.grant_date),
      patent_type: patent.patent_type || "",
      patent_status: patent.patent_status || "",
      academic_year: patent.academic_year || "",
      document_link: patent.document_link || "",
    });
    setEditingPatentId(patent.id);
  };

  const deletePatent = async (id) => {
    try {
      await axios.delete(`/profile/patents/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadPatents();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addCopyright = async () => {
    try {
      if (!copyrightForm.copyright_name.trim()) {
        showNotification("Copyright name is required", "error");
        return;
      }

      if (editingCopyrightId) {
        await axios.delete(`/profile/copyrights/${editingCopyrightId}`, { headers });
      }

      await axios.post("/profile/copyrights", copyrightForm, { headers });
      showNotification("Copyright added!");
      loadCopyrights();
      setCopyrightForm({ copyright_name: "", status: "", date_of_registration: "", academic_year: "", document_link: "" });
      setEditingCopyrightId(null);
    } catch (err) {
      showNotification("Failed to add copyright", "error");
    }
  };

  const startEditCopyright = (record) => {
    setCopyrightForm({
      copyright_name: record.copyright_name || "",
      status: record.status || "",
      date_of_registration: formatDateOnly(record.date_of_registration),
      academic_year: record.academic_year || "",
      document_link: record.document_link || "",
    });
    setEditingCopyrightId(record.id);
  };

  const deleteCopyright = async (id) => {
    try {
      await axios.delete(`/profile/copyrights/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadCopyrights();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addIndustrialDesign = async () => {
    try {
      if (!industrialDesignForm.design_name.trim()) {
        showNotification("Design name is required", "error");
        return;
      }

      if (editingIndustrialDesignId) {
        await axios.delete(`/profile/industrial-designs/${editingIndustrialDesignId}`, { headers });
      }

      await axios.post("/profile/industrial-designs", industrialDesignForm, { headers });
      showNotification("Industrial design added!");
      loadIndustrialDesigns();
      setIndustrialDesignForm({ design_name: "", design_number: "", date_of_grant: "", academic_year: "" });
      setEditingIndustrialDesignId(null);
    } catch (err) {
      showNotification("Failed to add industrial design", "error");
    }
  };

  const startEditIndustrialDesign = (record) => {
    setIndustrialDesignForm({
      design_name: record.design_name || "",
      design_number: record.design_number || "",
      date_of_grant: formatDateOnly(record.date_of_grant),
      academic_year: record.academic_year || "",
    });
    setEditingIndustrialDesignId(record.id);
  };

  const deleteIndustrialDesign = async (id) => {
    try {
      await axios.delete(`/profile/industrial-designs/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadIndustrialDesigns();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addJournal = async () => {
    try {
      if (editingJournalId) {
        await axios.delete(`/profile/journal-roles/${editingJournalId}`, { headers });
      }

      await axios.post("/profile/journal-roles", journalForm, { headers });
      showNotification("Journal role added!");
      loadJournals();
      setJournalForm({ role_type: "", journal_name: "", level: "", academic_year: "", letter_details: "", publisher: "" });
      setEditingJournalId(null);
    } catch (err) {
      showNotification("Failed to add journal role", "error");
    }
  };

  const startEditJournal = (journal) => {
    setJournalForm({
      role_type: journal.role_type || "",
      journal_name: journal.journal_name || "",
      level: journal.level || "",
      academic_year: journal.academic_year || "",
      letter_details: journal.letter_details || "",
      publisher: journal.publisher || "",
    });
    setEditingJournalId(journal.id);
  };

  const deleteJournal = async (id) => {
    try {
      await axios.delete(`/profile/journal-roles/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadJournals();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addFunding = async () => {
    try {
      if (editingFundingId) {
        await axios.delete(`/profile/external-funding/${editingFundingId}`, { headers });
      }
      await axios.post("/profile/external-funding", fundingForm, { headers });
      showNotification(editingFundingId ? "Funding updated!" : "Funding added!");
      loadFunding();
      setFundingForm({ agency_name: "", agency_type: "", amount: "", year: "", academic_year: "", letter_details: "" });
      setEditingFundingId(null);
    } catch (err) {
      showNotification(editingFundingId ? "Failed to update funding" : "Failed to add funding", "error");
    }
  };

  const startEditFunding = (fund) => {
    setFundingForm({
      agency_name: fund.agency_name || "",
      agency_type: fund.agency_type || "",
      amount: fund.amount || "",
      year: fund.year || "",
      academic_year: fund.academic_year || "",
      letter_details: fund.letter_details || "",
    });
    setEditingFundingId(fund.id);
  };

  const deleteFunding = async (id) => {
    try {
      await axios.delete(`/profile/external-funding/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadFunding();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addConsultancy = async () => {
    try {
      if (editingConsultancyId) {
        await axios.delete(`/profile/consultancy/${editingConsultancyId}`, { headers });
      }
      await axios.post("/profile/consultancy", consultancyForm, { headers });
      showNotification(editingConsultancyId ? "Consultancy updated!" : "Consultancy added!");
      loadConsultancy();
      setConsultancyForm({ industry_name: "", consultancy_type: "", amount: "", year: "", academic_year: "", consultancy_date: "", letter_details: "" });
      setEditingConsultancyId(null);
    } catch (err) {
      showNotification(editingConsultancyId ? "Failed to update consultancy" : "Failed to add consultancy", "error");
    }
  };

  const startEditConsultancy = (cons) => {
    setConsultancyForm({
      industry_name: cons.industry_name || "",
      consultancy_type: cons.consultancy_type || "",
      amount: cons.amount || "",
      year: cons.year || "",
      academic_year: cons.academic_year || "",
      consultancy_date: formatDateOnly(cons.consultancy_date),
      letter_details: cons.letter_details || "",
    });
    setEditingConsultancyId(cons.id);
  };

  const deleteConsultancy = async (id) => {
    try {
      await axios.delete(`/profile/consultancy/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadConsultancy();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addPhd = async () => {
    try {
      if (editingPhdId) {
        await axios.delete(`/profile/phd-scholars/${editingPhdId}`, { headers });
      }

      await axios.post("/profile/phd-scholars", phdForm, { headers });
      showNotification("PhD scholar added!");
      loadPhds();
      setPhdForm({ scholar_name: "", status: "", starting_year: "", ending_year: "", is_pursuing: false, academic_year: "", notification_details: "", thesis_title: "", document_link: "" });
      setEditingPhdId(null);
    } catch (err) {
      showNotification("Failed to add PhD scholar", "error");
    }
  };

  const startEditPhd = (phd) => {
    setPhdForm({
      scholar_name: phd.scholar_name || "",
      status: phd.status || "",
      starting_year: phd.starting_year || "",
      ending_year: phd.ending_year || "",
      is_pursuing: Boolean(phd.is_pursuing),
      academic_year: phd.academic_year || "",
      notification_details: phd.notification_details || "",
      thesis_title: phd.thesis_title || "",
      document_link: phd.document_link || "",
    });
    setEditingPhdId(phd.id);
  };

  const deletePhd = async (id) => {
    try {
      await axios.delete(`/profile/phd-scholars/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadPhds();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addSessionSpeaker = async () => {
    try {
      if (editingSessionSpeakerId) {
        await axios.delete(`/profile/session-speaker-details/${editingSessionSpeakerId}`, { headers });
      }

      await axios.post("/profile/session-speaker-details", sessionSpeakerForm, { headers });
      showNotification(editingSessionSpeakerId ? "Details updated!" : "Details added!");
      loadSessionSpeakers();
      setSessionSpeakerForm({ role: "", name_of_conference: "", institute_name: "", institute_nirf_ranking: "", mode: "Online", level: "", letter_no: "", date: "", academic_year: "", document_link: "" });
      setEditingSessionSpeakerId(null);
    } catch (err) {
      showNotification("Failed to save details", "error");
    }
  };

  const startEditSessionSpeaker = (record) => {
    setSessionSpeakerForm({
      role: record.role || "",
      name_of_conference: record.name_of_conference || "",
      institute_name: record.institute_name || "",
      institute_nirf_ranking: record.institute_nirf_ranking || "",
      mode: record.mode || "Online",
      level: record.level || "",
      letter_no: record.letter_no || "",
      date: record.date ? formatDateOnly(record.date) : "",
      academic_year: record.academic_year || "",
      document_link: record.document_link || "",
    });
    setEditingSessionSpeakerId(record.id);
  };

  const deleteSessionSpeaker = async (id) => {
    try {
      await axios.delete(`/profile/session-speaker-details/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadSessionSpeakers();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  return (
    <div className="space-y-8">
      {/* Personal Research Identifiers */}
      <Section title="Personal Research Identifiers">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <Input
            label="Google Scholar ID"
            value={googleScholarId}
            onChange={(e) => setGoogleScholarId(e.target.value)}
            placeholder="e.g. 8mYd4-wAAAAJ"
          />
          <Input
            label="Scopus Author ID"
            value={scopusId}
            onChange={(e) => setScopusId(e.target.value)}
            placeholder="e.g. 57211325988"
          />
          <Input
            label="ResearcherID (Web of Science)"
            value={wosId}
            onChange={(e) => setWosId(e.target.value)}
            placeholder="e.g. AAB-1234-2025"
          />
          <Input
            label="ORCID ID"
            value={orcidId}
            onChange={(e) => setOrcidId(e.target.value)}
            placeholder="e.g. 0000-0002-1825-0097"
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={saveResearchIdentifiers}
            disabled={identifiersLoading}
            className="btn-primary py-2 px-6 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
          >
            {identifiersLoading ? "Saving..." : "Save Identifiers"}
          </button>
        </div>
      </Section>

      {/* Research Publications */}
      <Section title="Research Publications">
        <div className="space-y-4">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <input type="radio" checked={researchMode === "doi"} onChange={() => setResearchMode("doi")} />
              Fetch With DOI
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <input type="radio" checked={researchMode === "manual"} onChange={() => setResearchMode("manual")} />
              Manual Entry
            </label>
          </div>

          {researchMode === "doi" && (
            <div className="flex gap-2">
              <Input
                label="DOI"
                value={doi}
                onChange={(e) => setDoi(e.target.value)}
                placeholder="10.1016/j.example.2023.1234 or https://doi.org/10.1016/..."
                className="flex-1"
              />
              <button onClick={fetchDOI} className="btn-secondary mt-6">Fetch DOI</button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Title" value={researchForm.title} onChange={(e) => setResearchForm({ ...researchForm, title: e.target.value })} disabled={isFetchedResearch} />
            <Input label="Authors" value={researchForm.authors} onChange={(e) => setResearchForm({ ...researchForm, authors: e.target.value })} disabled={isFetchedResearch} />
            <Input label="Journal / Conference / Book" value={researchForm.journal_name} onChange={(e) => setResearchForm({ ...researchForm, journal_name: e.target.value })} disabled={isFetchedResearch} />
            <SelectInput label="Publication Year" value={researchForm.publication_year} options={YEAR_OPTIONS} onChange={(e) => setResearchForm({ ...researchForm, publication_year: e.target.value })} disabled={isFetchedResearch} />
            <SelectInput label="Academic Year" value={researchForm.academic_year} options={ACADEMIC_YEAR_OPTIONS} required onChange={(e) => setResearchForm({ ...researchForm, academic_year: e.target.value })} />
            <SelectInput label="SVPCET Affiliation" value={researchForm.svpcet_affiliation} options={["Yes", "No"]} required onChange={(e) => setResearchForm({ ...researchForm, svpcet_affiliation: e.target.value })} />
            <Input label="DOI (Optional)" value={researchForm.doi} onChange={(e) => setResearchForm({ ...researchForm, doi: e.target.value })} disabled={isFetchedResearch} />
            <Input label="Publisher" value={researchForm.publisher} onChange={(e) => setResearchForm({ ...researchForm, publisher: e.target.value })} disabled={isFetchedResearch} />
            <Input label="Type" value={researchForm.type} onChange={(e) => setResearchForm({ ...researchForm, type: e.target.value })} disabled={isFetchedResearch} />
            <SelectInput label="Category" value={researchForm.category} options={["journal", "conference", "book-chapter", "other"]} onChange={(e) => setResearchForm({ ...researchForm, category: e.target.value })} disabled={isFetchedResearch} />
            <Input label="ISSN" value={researchForm.issn} onChange={(e) => setResearchForm({ ...researchForm, issn: e.target.value })} disabled={isFetchedResearch} />
            <Input label="URL" value={researchForm.url} onChange={(e) => setResearchForm({ ...researchForm, url: e.target.value })} disabled={isFetchedResearch} />
          </div>

          <div className="bg-indigo-50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Publication Indexing <span className="text-red-500">*</span></label>
            <div className="flex flex-wrap gap-6">
              {PUBLICATION_INDEXING_OPTIONS.map((indexingOption) => (
                <label key={indexingOption} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="indexing"
                    value={indexingOption}
                    checked={publicationIndexing === indexingOption}
                    onChange={(e) => setPublicationIndexing(e.target.value)}
                    className="w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-800 font-medium">{indexingOption}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 border-t border-indigo-200 pt-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Indexing Tags (multiple)</label>
              <div className="flex flex-wrap gap-6">
                {PUBLICATION_MULTI_INDEXING_OPTIONS.map((tag) => (
                  <label key={tag} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={publicationMultiIndexing.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPublicationMultiIndexing((prev) => [...prev, tag]);
                          return;
                        }

                        setPublicationMultiIndexing((prev) => prev.filter((item) => item !== tag));
                      }}
                      className="w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-800 font-medium">{tag}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={addResearch} className="btn-primary flex-1">Save To Profile</button>
            <button
              onClick={() => {
                setResearchData(null);
                setDoi("");
                setResearchForm({
                  title: "",
                  authors: "",
                  journal_name: "",
                  publication_year: "",
                  academic_year: "",
                  doi: "",
                  publisher: "",
                  type: "",
                  category: "other",
                  issn: "",
                  url: "",
                });
                setPublicationIndexing("Other");
                setPublicationMultiIndexing([]);
              }}
              className="btn-secondary"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Display saved research publications */}
        {researchList.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <h4 className="font-semibold text-gray-700 text-lg mb-3">Saved Publications</h4>
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Journal / Venue</th>
                  <th className="px-4 py-2 text-left">Publication Year</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">SVPCET Affiliation</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(researchList).map((research) => (
                  <tr key={research.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {research.url || research.doi ? (
                        <a href={research.url || `https://doi.org/${research.doi}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                          {research.title}
                        </a>
                      ) : (
                        research.title
                      )}
                    </td>
                    <td className="px-4 py-2">{research.journal_name || "N/A"}</td>
                    <td className="px-4 py-2">{research.publication_year || "N/A"}</td>
                    <td className="px-4 py-2">{research.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{research.svpcet_affiliation || "N/A"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                        research.verification_status === "verified" ? "bg-green-100 text-green-800" :
                        research.verification_status === "rejected" ? "bg-red-100 text-red-800" :
                        "bg-yellow-100 text-yellow-800"
                      }`}>
                        {research.verification_status || "unverified"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Delete research publication" variant="delete" onClick={() => deleteResearch(research.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Patents */}
      <Section title="Patents and Design">
        <DriveFolderHint message="Upload your patent supporting document using the dropbox below." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SelectInput
            label="Patent Type"
            value={patentForm.patent_type}
            options={PATENT_TYPE_OPTIONS}
            required
            onChange={(e) => setPatentForm({ ...patentForm, patent_type: e.target.value })}
          />
          <Input label="Patent Name" value={patentForm.patent_name} required
            onChange={(e) => setPatentForm({...patentForm, patent_name: e.target.value})} />
          <SelectInput
            label="Status"
            value={patentForm.patent_status}
            options={PATENT_STATUS_OPTIONS}
            required
            onChange={(e) => setPatentForm({ ...patentForm, patent_status: e.target.value })}
          />

          {/* Conditional fields based on status */}
          {patentForm.patent_status === "Filed" && (
            <>
              <Input label="Filing Number" value={patentForm.filing_no} required
                onChange={(e) => setPatentForm({...patentForm, filing_no: e.target.value})} />
              <Input label="Date Of Filing" type="date" value={patentForm.date_of_filing} required
                onChange={(e) => setPatentForm({...patentForm, date_of_filing: e.target.value})} />
            </>
          )}

          {patentForm.patent_status === "Published" && (
            <>
              <Input label="Application Number" value={patentForm.application_no} required
                onChange={(e) => setPatentForm({...patentForm, application_no: e.target.value})} />
              <Input label="Date Published" type="date" value={patentForm.date_published} required
                onChange={(e) => setPatentForm({...patentForm, date_published: e.target.value})} />
            </>
          )}

          {patentForm.patent_status === "Granted" && (
            <>
              <Input label="Grant Number" value={patentForm.grant_no} required
                onChange={(e) => setPatentForm({...patentForm, grant_no: e.target.value})} />
              <Input label="Grant Date" type="date" value={patentForm.grant_date} required
                onChange={(e) => setPatentForm({...patentForm, grant_date: e.target.value})} />
            </>
          )}

          <SelectInput
            label="Academic Year"
            value={patentForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            required
            onChange={(e) => setPatentForm({ ...patentForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={patentForm.document_link}
            onChange={(url) => setPatentForm({ ...patentForm, document_link: url })}
            headers={headers}
            section="patents"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addPatent} className="btn-primary">{editingPatentId ? "Update Patent" : "Add Patent"}</button>

        {patents.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Patent Type</th>
                  <th className="px-4 py-2 text-left">Patent Name</th>
                  <th className="px-4 py-2 text-left">Number</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(patents).map((patent) => (
                  <tr key={patent.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{patent.patent_type || "N/A"}</td>
                    <td className="px-4 py-2">{patent.patent_name}</td>
                    <td className="px-4 py-2">
                      {patent.patent_status === "Filed" && `Filing No: ${patent.filing_no || "N/A"}`}
                      {patent.patent_status === "Published" && `App No: ${patent.application_no || "N/A"}`}
                      {patent.patent_status === "Granted" && `Grant No: ${patent.grant_no || "N/A"}`}
                      {!["Filed", "Published", "Granted"].includes(patent.patent_status) && "N/A"}
                    </td>
                    <td className="px-4 py-2">
                      {patent.patent_status === "Filed" && (formatDateOnly(patent.date_of_filing) || "N/A")}
                      {patent.patent_status === "Published" && (formatDateOnly(patent.date_published) || "N/A")}
                      {patent.patent_status === "Granted" && (formatDateOnly(patent.grant_date) || "N/A")}
                      {!["Filed", "Published", "Granted"].includes(patent.patent_status) && "N/A"}
                    </td>
                    <td className="px-4 py-2">{patent.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{patent.patent_status || "N/A"}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit patent" variant="edit" onClick={() => startEditPatent(patent)} />
                        <ActionIconButton label="Delete patent" variant="delete" onClick={() => deletePatent(patent.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Copyright Records */}
      <Section title="Copyright Records">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Copyright Name" value={copyrightForm.copyright_name}
            onChange={(e) => setCopyrightForm({ ...copyrightForm, copyright_name: e.target.value })} />
          <SelectInput
            label="Status"
            value={copyrightForm.status}
            options={COPYRIGHT_STATUS_OPTIONS}
            onChange={(e) => setCopyrightForm({ ...copyrightForm, status: e.target.value })}
          />
          <Input label="Date of Registration" type="date" value={copyrightForm.date_of_registration}
            onChange={(e) => setCopyrightForm({ ...copyrightForm, date_of_registration: e.target.value })} />
          <SelectInput
            label="Academic Year"
            value={copyrightForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setCopyrightForm({ ...copyrightForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={copyrightForm.document_link}
            onChange={(url) => setCopyrightForm({ ...copyrightForm, document_link: url })}
            headers={headers}
            section="copyright"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addCopyright} className="btn-primary">{editingCopyrightId ? "Update Copyright" : "Add Copyright"}</button>

        {copyrights.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(copyrights).map((record) => (
                  <tr key={record.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{record.copyright_name}</td>
                    <td className="px-4 py-2">{record.status || "N/A"}</td>
                    <td className="px-4 py-2">{formatDateOnly(record.date_of_registration) || "N/A"}</td>
                    <td className="px-4 py-2">{record.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit copyright" variant="edit" onClick={() => startEditCopyright(record)} />
                        <ActionIconButton label="Delete copyright" variant="delete" onClick={() => deleteCopyright(record.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Industrial Designs */}
      {/*
      <Section title="Industrial Designs">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Name of Design" value={industrialDesignForm.design_name}
            onChange={(e) => setIndustrialDesignForm({ ...industrialDesignForm, design_name: e.target.value })} />
          <Input label="Design Number" value={industrialDesignForm.design_number}
            onChange={(e) => setIndustrialDesignForm({ ...industrialDesignForm, design_number: e.target.value })} />
          <Input label="Date of Grant" type="date" value={industrialDesignForm.date_of_grant}
            onChange={(e) => setIndustrialDesignForm({ ...industrialDesignForm, date_of_grant: e.target.value })} />
          <SelectInput
            label="Academic Year"
            value={industrialDesignForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setIndustrialDesignForm({ ...industrialDesignForm, academic_year: e.target.value })}
          />
        </div>
        <button onClick={addIndustrialDesign} className="btn-primary">{editingIndustrialDesignId ? "Update Industrial Design" : "Add Industrial Design"}</button>

        {industrialDesigns.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Design Number</th>
                  <th className="px-4 py-2 text-left">Grant Date</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(industrialDesigns).map((design) => (
                  <tr key={design.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{design.design_name}</td>
                    <td className="px-4 py-2">{design.design_number || "N/A"}</td>
                    <td className="px-4 py-2">{formatDateOnly(design.date_of_grant) || "N/A"}</td>
                    <td className="px-4 py-2">{design.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit design" variant="edit" onClick={() => startEditIndustrialDesign(design)} />
                        <ActionIconButton label="Delete design" variant="delete" onClick={() => deleteIndustrialDesign(design.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      */}

      {/* Journal Roles */}
      <Section title="Journal Editorial Roles">
        <DriveFolderHint />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SelectInput
            label="Role Type"
            value={journalForm.role_type}
            options={["Reviewer", "Editor", "Associate Editor", "Editorial Board", "Guest Editor"]}
            onChange={(e) => setJournalForm({ ...journalForm, role_type: e.target.value })}
          />
          <Input label="Journal Name" value={journalForm.journal_name}
            onChange={(e) => setJournalForm({...journalForm, journal_name: e.target.value})} />
          <Input label="Publisher" value={journalForm.publisher}
            onChange={(e) => setJournalForm({...journalForm, publisher: e.target.value})} />
          <SelectInput
            label="Level"
            value={journalForm.level}
            options={["National", "International", "Scopus", "SCI/SCIE", "UGC Care"]}
            onChange={(e) => setJournalForm({ ...journalForm, level: e.target.value })}
          />
          <SelectInput
            label="Academic Year"
            value={journalForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setJournalForm({ ...journalForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={journalForm.letter_details}
            onChange={(url) => setJournalForm({...journalForm, letter_details: url})}
            headers={headers}
            section="journal-roles"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addJournal} className="btn-primary">{editingJournalId ? "Update Journal Role" : "Add Journal Role"}</button>

        {journals.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Journal Name</th>
                  <th className="px-4 py-2 text-left">Role Type</th>
                  <th className="px-4 py-2 text-left">Level</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(journals).map((journal) => (
                  <tr key={journal.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{journal.journal_name}</td>
                    <td className="px-4 py-2">{journal.role_type || "N/A"}</td>
                    <td className="px-4 py-2">{journal.level || "N/A"}</td>
                    <td className="px-4 py-2">{journal.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit journal role" variant="edit" onClick={() => startEditJournal(journal)} />
                        <ActionIconButton label="Delete journal role" variant="delete" onClick={() => deleteJournal(journal.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* External Funding */}
      <Section title="External Funding">
        <DriveFolderHint />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Agency Name" value={fundingForm.agency_name}
            onChange={(e) => setFundingForm({...fundingForm, agency_name: e.target.value})} />
          <SelectInput
            label="Agency Type"
            value={fundingForm.agency_type}
            options={["Government", "Private", "International", "Institute", "Industry"]}
            onChange={(e) => setFundingForm({ ...fundingForm, agency_type: e.target.value })}
          />
          <Input label="Amount" value={fundingForm.amount}
            onChange={(e) => setFundingForm({...fundingForm, amount: e.target.value})} />
          <Input label="Year" value={fundingForm.year}
            onChange={(e) => setFundingForm({...fundingForm, year: e.target.value})} />
          <SelectInput
            label="Academic Year"
            value={fundingForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setFundingForm({ ...fundingForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={fundingForm.letter_details}
            onChange={(url) => setFundingForm({...fundingForm, letter_details: url})}
            headers={headers}
            section="external-funding"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addFunding} className="btn-primary">{editingFundingId ? "Update Funding" : "Add Funding"}</button>

        {funding.length > 0 && (
          <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Agency</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Amount</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Year</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(funding).map((fund) => (
                  <tr key={fund.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-semibold">{fund.agency_name || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fund.agency_type || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fund.amount ? `₹${fund.amount}` : "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fund.year || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{fund.academic_year || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        fund.verification_status === "verified"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {fund.verification_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit funding" variant="edit" onClick={() => startEditFunding(fund)} />
                        <ActionIconButton label="Delete funding" variant="delete" onClick={() => deleteFunding(fund.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Consultancy */}
      <Section title="Consultancy Revenue">
        <DriveFolderHint />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Industry Name" value={consultancyForm.industry_name}
            onChange={(e) => setConsultancyForm({...consultancyForm, industry_name: e.target.value})} />
          <SelectInput label="Consultancy Type" value={consultancyForm.consultancy_type}
            options={["Consultancy", "Testing", "Other"]}
            onChange={(e) => setConsultancyForm({...consultancyForm, consultancy_type: e.target.value})} />
          <Input label="Amount" value={consultancyForm.amount}
            onChange={(e) => setConsultancyForm({...consultancyForm, amount: e.target.value})} />
          <SelectInput
            label="Academic Year"
            value={consultancyForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setConsultancyForm({ ...consultancyForm, academic_year: e.target.value })}
          />
          <Input label="Date" type="date" value={consultancyForm.consultancy_date}
            onChange={(e) => setConsultancyForm({...consultancyForm, consultancy_date: e.target.value})} />
          <DocumentDropbox
            label="Supporting Document"
            value={consultancyForm.letter_details}
            onChange={(url) => setConsultancyForm({...consultancyForm, letter_details: url})}
            headers={headers}
            section="consultancy"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addConsultancy} className="btn-primary">{editingConsultancyId ? "Update Consultancy" : "Add Consultancy"}</button>

        {consultancy.length > 0 && (
          <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Industry</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Amount</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(consultancy).map((cons) => (
                  <tr key={cons.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-semibold">{cons.industry_name || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{cons.consultancy_type || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{cons.amount ? `₹${cons.amount}` : "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{cons.academic_year || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{formatDateOnly(cons.consultancy_date) || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        cons.verification_status === "verified"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {cons.verification_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit consultancy" variant="edit" onClick={() => startEditConsultancy(cons)} />
                        <ActionIconButton label="Delete consultancy" variant="delete" onClick={() => deleteConsultancy(cons.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* PhD Scholars */}
      <Section title="PhD Scholars">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Scholar Name" value={phdForm.scholar_name}
            onChange={(e) => setPhdForm({...phdForm, scholar_name: e.target.value})} />
          <SelectInput
            label="Status"
            value={phdForm.status}
            options={["Pursuing", "Submitted", "Awarded", "Completed", "Discontinued"]}
            onChange={(e) => setPhdForm({ ...phdForm, status: e.target.value })}
          />
          <Input label="Starting Year" type="number" value={phdForm.starting_year}
            onChange={(e) => setPhdForm({ ...phdForm, starting_year: e.target.value })} />
          <div className="flex items-center gap-2 pt-7">
            <input
              type="checkbox"
              checked={phdForm.is_pursuing}
              onChange={(e) => setPhdForm({ ...phdForm, is_pursuing: e.target.checked, ending_year: e.target.checked ? "" : phdForm.ending_year })}
              className="h-4 w-4"
            />
            <span className="text-sm font-medium text-gray-700">Currently Pursuing</span>
          </div>
          {!phdForm.is_pursuing && (
            <Input label="Ending Year" type="number" value={phdForm.ending_year}
              onChange={(e) => setPhdForm({...phdForm, ending_year: e.target.value})} />
          )}
          <SelectInput
            label="Academic Year"
            value={phdForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setPhdForm({ ...phdForm, academic_year: e.target.value })}
          />
          <Input label="Notification Details" value={phdForm.notification_details}
            onChange={(e) => setPhdForm({...phdForm, notification_details: e.target.value})} />
          <Input label="Thesis Title" value={phdForm.thesis_title}
            onChange={(e) => setPhdForm({...phdForm, thesis_title: e.target.value})} />
          <DocumentDropbox
            label="Supporting Document"
            value={phdForm.document_link}
            onChange={(url) => setPhdForm({ ...phdForm, document_link: url })}
            headers={headers}
            section="phd-scholar"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addPhd} className="btn-primary">{editingPhdId ? "Update PhD Scholar" : "Add PhD Scholar"}</button>

        {phds.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Scholar Name</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Starting Year</th>
                  <th className="px-4 py-2 text-left">Ending Year</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Thesis Title</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(phds).map((phd) => (
                  <tr key={phd.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{phd.scholar_name}</td>
                    <td className="px-4 py-2">{phd.status || "N/A"}</td>
                    <td className="px-4 py-2">{phd.starting_year || "N/A"}</td>
                    <td className="px-4 py-2">{phd.is_pursuing ? "Pursuing" : (phd.ending_year || "N/A")}</td>
                    <td className="px-4 py-2">{phd.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{phd.thesis_title || "N/A"}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit PhD scholar" variant="edit" onClick={() => startEditPhd(phd)} />
                        <ActionIconButton label="Delete PhD scholar" variant="delete" onClick={() => deletePhd(phd.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Session chair / keynote speaker details */}
      <Section title="Session chair / keynote speaker details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SelectInput
            label="Role"
            value={sessionSpeakerForm.role}
            options={["Session Chair", "Keynote Speaker", "Guest Speaker", "Other"]}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, role: e.target.value })}
          />
          <Input label="Name of Conference" value={sessionSpeakerForm.name_of_conference}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, name_of_conference: e.target.value })} />
          <Input label="Institute Name" value={sessionSpeakerForm.institute_name}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, institute_name: e.target.value })} />
          <Input label="Institute NIRF Ranking" value={sessionSpeakerForm.institute_nirf_ranking}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, institute_nirf_ranking: e.target.value })} />
          <SelectInput
            label="Mode"
            value={sessionSpeakerForm.mode}
            options={["Online", "Offline"]}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, mode: e.target.value })}
          />
          <SelectInput
            label="Level"
            value={sessionSpeakerForm.level}
            options={["National", "International", "State", "University"]}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, level: e.target.value })}
          />
          <Input label="Letter No" value={sessionSpeakerForm.letter_no}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, letter_no: e.target.value })} />
          <Input label="Date" type="date" value={sessionSpeakerForm.date}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, date: e.target.value })} />
          <SelectInput
            label="Academic Year"
            value={sessionSpeakerForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setSessionSpeakerForm({ ...sessionSpeakerForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={sessionSpeakerForm.document_link}
            onChange={(url) => setSessionSpeakerForm({ ...sessionSpeakerForm, document_link: url })}
            headers={headers}
            section="session-speaker"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addSessionSpeaker} className="btn-primary">
          {editingSessionSpeakerId ? "Update Details" : "Add Details"}
        </button>

        {sessionSpeakerList.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Conference Name</th>
                  <th className="px-4 py-2 text-left">Institute</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(sessionSpeakerList).map((record) => (
                  <tr key={record.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{record.role || "N/A"}</td>
                    <td className="px-4 py-2">{record.name_of_conference || "N/A"}</td>
                    <td className="px-4 py-2">{record.institute_name || "N/A"}</td>
                    <td className="px-4 py-2">{record.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{formatDateOnly(record.date)}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit details" variant="edit" onClick={() => startEditSessionSpeaker(record)} />
                        <ActionIconButton label="Delete details" variant="delete" onClick={() => deleteSessionSpeaker(record.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

/* ===============================================
   FDP & TRAINING TAB
=============================================== */
function FDPTraining({ headers, showNotification }) {
  const [organizedForm, setOrganizedForm] = useState({ 
    title: "", mode: "Online", sponsorship_type: "", duration_value: "", duration_unit: "Hours", academic_year: "", start_date: "", end_date: "", document_link: "" 
  });
  const [attendedForm, setAttendedForm] = useState({ 
    title: "", institute_name: "", nirf_rank: "", mode: "Online", duration_value: "", duration_unit: "Hours", academic_year: "", start_date: "", end_date: "", document_link: "" 
  });
  const [moocForm, setMoocForm] = useState({
    mooc_econtent: "",
    title_of_content: "",
    credits: "",
    semester_students: "",
    academic_year: "",
    document_link: "",
  });
  const [swayamForm, setSwayamForm] = useState({
    agency_name: "",
    course_title: "",
    rank: "",
    grade_percentage: "",
    academic_year: "",
    document_link: "",
  });

  const [organized, setOrganized] = useState([]);
  const [attended, setAttended] = useState([]);
  const [moocs, setMoocs] = useState([]);
  const [swayamCertifications, setSwayamCertifications] = useState([]);
  const [editingOrganizedId, setEditingOrganizedId] = useState(null);
  const [editingAttendedId, setEditingAttendedId] = useState(null);
  const [editingMoocId, setEditingMoocId] = useState(null);
  const [editingSwayamId, setEditingSwayamId] = useState(null);

  const [resourcePersonForm, setResourcePersonForm] = useState({ institute_name: "", institute_nirf_ranking: "", title: "", mode: "Online", letter_no: "", date: "", academic_year: "", document_link: "" });
  const [resourcePersonList, setResourcePersonList] = useState([]);
  const [editingResourcePersonId, setEditingResourcePersonId] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    loadOrganized();
    loadAttended();
    loadMoocs();
    loadSwayamCertifications();
    loadResourcePersons();
  };

  const loadOrganized = async () => {
    try {
      const res = await axios.get("/profile/fdp-organized", { headers });
      setOrganized(res.data || []);
    } catch (err) {}
  };

  const loadAttended = async () => {
    try {
      const res = await axios.get("/profile/fdp-attended", { headers });
      setAttended(res.data || []);
    } catch (err) {}
  };

  const loadMoocs = async () => {
    try {
      const res = await axios.get("/profile/mooc-econtent-developed", { headers });
      setMoocs(res.data || []);
    } catch (err) {}
  };

  const loadSwayamCertifications = async () => {
    try {
      const res = await axios.get("/profile/swayam-mooc-certifications", { headers });
      setSwayamCertifications(res.data || []);
    } catch (err) {}
  };

  const loadResourcePersons = async () => {
    try {
      const res = await axios.get("/profile/fdp-resource-person", { headers });
      setResourcePersonList(res.data || []);
    } catch (err) {}
  };

  const addOrganized = async () => {
    try {
      if (editingOrganizedId) {
        await axios.delete(`/profile/fdp-organized/${editingOrganizedId}`, { headers });
      }

      const formData = new FormData();
      formData.append("title", organizedForm.title);
      formData.append("mode", organizedForm.mode);
      formData.append("sponsorship_type", organizedForm.sponsorship_type);
      formData.append("duration_value", organizedForm.duration_value);
      formData.append("duration_unit", organizedForm.duration_unit);
      formData.append("academic_year", organizedForm.academic_year);
      formData.append("duration_details", organizedForm.duration_value ? `${organizedForm.duration_value} ${organizedForm.duration_unit}` : "");
      formData.append("start_date", organizedForm.start_date);
      formData.append("end_date", organizedForm.end_date);
      formData.append("document_link", organizedForm.document_link);

      await axios.post("/profile/fdp-organized", formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });

      showNotification(editingOrganizedId ? "FDP organized updated!" : "FDP organized added!");
      loadOrganized();
      setOrganizedForm({ title: "", mode: "Online", sponsorship_type: "", duration_value: "", duration_unit: "Hours", academic_year: "", start_date: "", end_date: "", document_link: "" });
      setEditingOrganizedId(null);
    } catch (err) {
      showNotification("Failed to add FDP", "error");
    }
  };

  const deleteOrganized = async (id) => {
    try {
      await axios.delete(`/profile/fdp-organized/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadOrganized();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addAttended = async () => {
    try {
      if (editingAttendedId) {
        await axios.delete(`/profile/fdp-attended/${editingAttendedId}`, { headers });
      }

      const formData = new FormData();
      formData.append("title", attendedForm.title);
      formData.append("institute_name", attendedForm.institute_name);
      formData.append("nirf_rank", attendedForm.nirf_rank);
      formData.append("mode", attendedForm.mode);
      formData.append("duration_value", attendedForm.duration_value);
      formData.append("duration_unit", attendedForm.duration_unit);
      formData.append("academic_year", attendedForm.academic_year);
      formData.append("duration_details", attendedForm.duration_value ? `${attendedForm.duration_value} ${attendedForm.duration_unit}` : "");
      formData.append("start_date", attendedForm.start_date);
      formData.append("end_date", attendedForm.end_date);
      formData.append("document_link", attendedForm.document_link);

      await axios.post("/profile/fdp-attended", formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });

      showNotification(editingAttendedId ? "FDP attended updated!" : "FDP attended added!");
      loadAttended();
      setAttendedForm({ title: "", institute_name: "", nirf_rank: "", mode: "Online", duration_value: "", duration_unit: "Hours", academic_year: "", start_date: "", end_date: "", document_link: "" });
      setEditingAttendedId(null);
    } catch (err) {
      showNotification("Failed to add FDP", "error");
    }
  };

  const deleteAttended = async (id) => {
    try {
      await axios.delete(`/profile/fdp-attended/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadAttended();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addMooc = async () => {
    try {
      if (editingMoocId) {
        await axios.put(`/profile/mooc-econtent-developed/${editingMoocId}`, moocForm, { headers });
        showNotification("MOOC/E-content updated!");
      } else {
        await axios.post("/profile/mooc-econtent-developed", moocForm, { headers });
        showNotification("MOOC/E-content added!");
      }
      loadMoocs();
      setMoocForm({ mooc_econtent: "", title_of_content: "", credits: "", semester_students: "", academic_year: "", document_link: "" });
      setEditingMoocId(null);
    } catch (err) {
      showNotification(editingMoocId ? "Failed to update MOOC/E-content" : "Failed to add MOOC/E-content", "error");
    }
  };

  const deleteMooc = async (id) => {
    try {
      await axios.delete(`/profile/mooc-econtent-developed/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadMoocs();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const cancelMoocEdit = () => {
    setEditingMoocId(null);
    setMoocForm({ mooc_econtent: "", title_of_content: "", credits: "", semester_students: "", academic_year: "", document_link: "" });
  };

  const addSwayamCertification = async () => {
    try {
      if (editingSwayamId) {
        await axios.put(`/profile/swayam-mooc-certifications/${editingSwayamId}`, swayamForm, { headers });
        showNotification("SWAYAM certification updated!");
      } else {
        await axios.post("/profile/swayam-mooc-certifications", swayamForm, { headers });
        showNotification("SWAYAM certification added!");
      }
      loadSwayamCertifications();
      setSwayamForm({ agency_name: "", course_title: "", rank: "", grade_percentage: "", academic_year: "", document_link: "" });
      setEditingSwayamId(null);
    } catch (err) {
      showNotification(editingSwayamId ? "Failed to update SWAYAM certification" : "Failed to add SWAYAM certification", "error");
    }
  };

  const deleteSwayamCertification = async (id) => {
    try {
      await axios.delete(`/profile/swayam-mooc-certifications/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadSwayamCertifications();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addResourcePerson = async () => {
    try {
      if (editingResourcePersonId) {
        await axios.delete(`/profile/fdp-resource-person/${editingResourcePersonId}`, { headers });
      }

      await axios.post("/profile/fdp-resource-person", resourcePersonForm, { headers });
      showNotification(editingResourcePersonId ? "Resource person details updated!" : "Resource person details added!");
      loadResourcePersons();
      setResourcePersonForm({ institute_name: "", institute_nirf_ranking: "", title: "", mode: "Online", letter_no: "", date: "", academic_year: "", document_link: "" });
      setEditingResourcePersonId(null);
    } catch (err) {
      showNotification("Failed to save details", "error");
    }
  };

  const startEditResourcePerson = (record) => {
    setResourcePersonForm({
      institute_name: record.institute_name || "",
      institute_nirf_ranking: record.institute_nirf_ranking || "",
      title: record.title || "",
      mode: record.mode || "Online",
      letter_no: record.letter_no || "",
      date: record.date ? formatDateOnly(record.date) : "",
      academic_year: record.academic_year || "",
      document_link: record.document_link || "",
    });
    setEditingResourcePersonId(record.id);
  };

  const deleteResourcePerson = async (id) => {
    try {
      await axios.delete(`/profile/fdp-resource-person/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadResourcePersons();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  return (
    <div className="space-y-8">
      {/* FDP Organized */}
      <Section title="FDP Organized">
        <DriveFolderHint message="Upload your FDP supporting document using the dropbox below." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Program Title" value={organizedForm.title}
            onChange={(e) => setOrganizedForm({...organizedForm, title: e.target.value})} />
          <select 
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-8"
            value={organizedForm.mode}
            onChange={(e) => setOrganizedForm({...organizedForm, mode: e.target.value})}>
            <option value="Online">Online</option>
            <option value="Offline">Offline</option>
          </select>
          <SelectInput
            label="Sponsorship Type"
            value={organizedForm.sponsorship_type}
            options={SPONSORSHIP_TYPE_OPTIONS}
            onChange={(e) => setOrganizedForm({ ...organizedForm, sponsorship_type: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Duration Value" type="number" value={organizedForm.duration_value}
              onChange={(e) => setOrganizedForm({...organizedForm, duration_value: e.target.value})} />
            <SelectInput
              label="Duration Unit"
              value={organizedForm.duration_unit}
              options={DURATION_UNIT_OPTIONS.concat(["Hours"])}
              onChange={(e) => setOrganizedForm({ ...organizedForm, duration_unit: e.target.value })}
            />
          </div>
          <SelectInput
            label="Academic Year"
            value={organizedForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setOrganizedForm({ ...organizedForm, academic_year: e.target.value })}
          />
          <Input label="Start Date" type="date" value={organizedForm.start_date}
            onChange={(e) => setOrganizedForm({...organizedForm, start_date: e.target.value})} />
          <Input label="End Date" type="date" value={organizedForm.end_date}
            onChange={(e) => setOrganizedForm({...organizedForm, end_date: e.target.value})} />
          <DocumentDropbox
            label="FDP Organized Document"
            value={organizedForm.document_link}
            onChange={(url) => setOrganizedForm({ ...organizedForm, document_link: url })}
            headers={headers}
            section="fdp-organized"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addOrganized} className="btn-primary">{editingOrganizedId ? "Update FDP Organized" : "Add FDP Organized"}</button>

        {organized.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Program Title</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Date Range</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(organized).map((fdp) => (
                  <tr key={fdp.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{fdp.title}</td>
                    <td className="px-4 py-2">{fdp.duration_details || "N/A"}</td>
                    <td className="px-4 py-2">{fdp.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{formatDateOnly(fdp.start_date)} to {formatDateOnly(fdp.end_date)}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit FDP organized" variant="edit" onClick={() => {
                          setOrganizedForm({
                          title: fdp.title || "",
                          mode: fdp.mode || "Online",
                          sponsorship_type: fdp.sponsorship_type || "",
                          duration_value: fdp.duration_value || "",
                          duration_unit: fdp.duration_unit || "Hours",
                          academic_year: fdp.academic_year || "",
                          start_date: formatDateOnly(fdp.start_date),
                          end_date: formatDateOnly(fdp.end_date),
                          document_link: fdp.document_link || "",
                        });
                          setEditingOrganizedId(fdp.id);
                        }} />
                        <ActionIconButton label="Delete FDP organized" variant="delete" onClick={() => deleteOrganized(fdp.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* FDP Attended */}
      <Section title="FDP Attended">
        <DriveFolderHint message="Upload your FDP supporting document using the dropbox below." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Program Title" value={attendedForm.title}
            onChange={(e) => setAttendedForm({...attendedForm, title: e.target.value})} />
          <Input label="Institute Name" value={attendedForm.institute_name}
            onChange={(e) => setAttendedForm({...attendedForm, institute_name: e.target.value})} />
          <Input label="NIRF Rank" value={attendedForm.nirf_rank}
            onChange={(e) => setAttendedForm({...attendedForm, nirf_rank: e.target.value})} />
          <select 
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-8"
            value={attendedForm.mode}
            onChange={(e) => setAttendedForm({...attendedForm, mode: e.target.value})}>
            <option value="Online">Online</option>
            <option value="Offline">Offline</option>
          </select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Duration Value" type="number" value={attendedForm.duration_value}
              onChange={(e) => setAttendedForm({...attendedForm, duration_value: e.target.value})} />
            <SelectInput
              label="Duration Unit"
              value={attendedForm.duration_unit}
              options={DURATION_UNIT_OPTIONS.concat(["Hours"])}
              onChange={(e) => setAttendedForm({ ...attendedForm, duration_unit: e.target.value })}
            />
          </div>
          <SelectInput
            label="Academic Year"
            value={attendedForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setAttendedForm({ ...attendedForm, academic_year: e.target.value })}
          />
          <Input label="Start Date" type="date" value={attendedForm.start_date}
            onChange={(e) => setAttendedForm({...attendedForm, start_date: e.target.value})} />
          <Input label="End Date" type="date" value={attendedForm.end_date}
            onChange={(e) => setAttendedForm({...attendedForm, end_date: e.target.value})} />
          <DocumentDropbox
            label="FDP Attended Document"
            value={attendedForm.document_link}
            onChange={(url) => setAttendedForm({ ...attendedForm, document_link: url })}
            headers={headers}
            section="fdp-attended"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addAttended} className="btn-primary">{editingAttendedId ? "Update FDP Attended" : "Add FDP Attended"}</button>

        {attended.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Program Title</th>
                  <th className="px-4 py-2 text-left">Institute</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Date Range</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(attended).map((fdp) => (
                  <tr key={fdp.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{fdp.title}</td>
                    <td className="px-4 py-2">{fdp.institute_name}</td>
                    <td className="px-4 py-2">{fdp.duration_details || "N/A"}</td>
                    <td className="px-4 py-2">{fdp.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{formatDateOnly(fdp.start_date)} to {formatDateOnly(fdp.end_date)}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit FDP attended" variant="edit" onClick={() => {
                          setAttendedForm({
                          title: fdp.title || "",
                          institute_name: fdp.institute_name || "",
                          nirf_rank: fdp.nirf_rank || "",
                          mode: fdp.mode || "Online",
                          duration_value: fdp.duration_value || "",
                          duration_unit: fdp.duration_unit || "Hours",
                          academic_year: fdp.academic_year || "",
                          start_date: formatDateOnly(fdp.start_date),
                          end_date: formatDateOnly(fdp.end_date),
                          document_link: fdp.document_link || "",
                        });
                          setEditingAttendedId(fdp.id);
                        }} />
                        <ActionIconButton label="Delete FDP attended" variant="delete" onClick={() => deleteAttended(fdp.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* MOOCs / E-Content Developed */}
      <Section title="MOOCs / E-content Developed">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SelectInput label="MOOC / E-Content" value={moocForm.mooc_econtent} options={MOOC_CONTENT_OPTIONS}
            onChange={(e) => setMoocForm({ ...moocForm, mooc_econtent: e.target.value })} />
          <Input label="Title of the Course / Content" value={moocForm.title_of_content}
            onChange={(e) => setMoocForm({ ...moocForm, title_of_content: e.target.value })} />
          <Input label="Credits" value={moocForm.credits}
            onChange={(e) => setMoocForm({ ...moocForm, credits: e.target.value })} />
          <SelectInput label="For Which Semester Students" value={moocForm.semester_students} options={MOOC_SEMESTER_OPTIONS}
            onChange={(e) => setMoocForm({ ...moocForm, semester_students: e.target.value })} />
          <SelectInput label="Academic Year" value={moocForm.academic_year} options={ACADEMIC_YEAR_OPTIONS} required
            onChange={(e) => setMoocForm({ ...moocForm, academic_year: e.target.value })} />
          <DocumentDropbox
            label="Supporting Document"
            value={moocForm.document_link}
            onChange={(url) => setMoocForm({ ...moocForm, document_link: url })}
            headers={headers}
            section="mooc-econtent"
            showNotification={showNotification}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={addMooc} className="btn-primary">{editingMoocId ? "Update MOOC / E-content" : "Add MOOC / E-content"}</button>
          {editingMoocId && (
            <button onClick={cancelMoocEdit} className="btn-secondary">Cancel</button>
          )}
        </div>

        {moocs.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Credits</th>
                  <th className="px-4 py-2 text-left">Semester</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Document</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(moocs).map((record) => (
                  <tr key={record.id} className={`border-t border-gray-200 hover:bg-gray-50 ${record.verification_status === "rejected" ? "bg-red-50" : ""}`}>
                    <td className="px-4 py-2">{record.content_type || "N/A"}</td>
                    <td className="px-4 py-2">{record.content_title || "N/A"}</td>
                    <td className="px-4 py-2">{record.credits || "N/A"}</td>
                    <td className="px-4 py-2">{record.semester_students || "N/A"}</td>
                    <td className="px-4 py-2">{record.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">
                      {record.document_link ? (
                        <a href={buildProtectedDocumentUrl(record.document_link)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">View Document</a>
                      ) : "N/A"}
                    </td>
                    <td className="px-4 py-2"><VerificationStatusCell record={record} /></td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit MOOC record" variant="edit" onClick={() => {
                          setMoocForm({
                            mooc_econtent: record.content_type || "",
                            title_of_content: record.content_title || "",
                            credits: record.credits || "",
                            semester_students: record.semester_students || "",
                            academic_year: record.academic_year || "",
                            document_link: record.document_link || "",
                          });
                          setEditingMoocId(record.id);
                        }} />
                        <ActionIconButton label="Delete MOOC record" variant="delete" onClick={() => deleteMooc(record.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* SWAYAM MOOC Certification */}
      <Section title="SWAYAM MOOC Certification">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Name of Agency" value={swayamForm.agency_name}
            onChange={(e) => setSwayamForm({ ...swayamForm, agency_name: e.target.value })} />
          <Input label="Title of the Course" value={swayamForm.course_title}
            onChange={(e) => setSwayamForm({ ...swayamForm, course_title: e.target.value })} />
          <Input label="Rank" value={swayamForm.rank}
            onChange={(e) => setSwayamForm({ ...swayamForm, rank: e.target.value })} />
          <Input label="Grade / Percentage" value={swayamForm.grade_percentage}
            onChange={(e) => setSwayamForm({ ...swayamForm, grade_percentage: e.target.value })} />
          <SelectInput label="Academic Year" value={swayamForm.academic_year} options={ACADEMIC_YEAR_OPTIONS} required
            onChange={(e) => setSwayamForm({ ...swayamForm, academic_year: e.target.value })} />
          <DocumentDropbox
            label="Supporting Document"
            value={swayamForm.document_link}
            onChange={(url) => setSwayamForm({ ...swayamForm, document_link: url })}
            headers={headers}
            section="swayam-mooc"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addSwayamCertification} className="btn-primary">{editingSwayamId ? "Update SWAYAM Certification" : "Add SWAYAM Certification"}</button>

        {swayamCertifications.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Agency</th>
                  <th className="px-4 py-2 text-left">Course Title</th>
                  <th className="px-4 py-2 text-left">Rank</th>
                  <th className="px-4 py-2 text-left">Grade / Percentage</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Document</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(swayamCertifications).map((record) => (
                  <tr key={record.id} className={`border-t border-gray-200 hover:bg-gray-50 ${record.verification_status === "rejected" ? "bg-red-50" : ""}`}>
                    <td className="px-4 py-2">{record.agency_name || "N/A"}</td>
                    <td className="px-4 py-2">{record.course_title || "N/A"}</td>
                    <td className="px-4 py-2">{record.rank || "N/A"}</td>
                    <td className="px-4 py-2">{record.grade_percentage || "N/A"}</td>
                    <td className="px-4 py-2">{record.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">
                      {record.document_link ? (
                        <a href={buildProtectedDocumentUrl(record.document_link)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">View Document</a>
                      ) : "N/A"}
                    </td>
                    <td className="px-4 py-2"><VerificationStatusCell record={record} /></td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit SWAYAM record" variant="edit" onClick={() => {
                          setSwayamForm({
                            agency_name: record.agency_name || "",
                            course_title: record.course_title || "",
                            rank: record.rank || "",
                            grade_percentage: record.grade_percentage || "",
                            academic_year: record.academic_year || "",
                            document_link: record.document_link || "",
                          });
                          setEditingSwayamId(record.id);
                        }} />
                        <ActionIconButton label="Delete SWAYAM record" variant="delete" onClick={() => deleteSwayamCertification(record.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Resource person in FDP */}
      <Section title="Resource person in FDP">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Name of the Institute" value={resourcePersonForm.institute_name}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, institute_name: e.target.value })} />
          <Input label="Institute NIRF Ranking" value={resourcePersonForm.institute_nirf_ranking}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, institute_nirf_ranking: e.target.value })} />
          <Input label="Title of Talk / Session" value={resourcePersonForm.title}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, title: e.target.value })} />
          <SelectInput
            label="Mode"
            value={resourcePersonForm.mode}
            options={["Online", "Offline"]}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, mode: e.target.value })}
          />
          <Input label="Letter No" value={resourcePersonForm.letter_no}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, letter_no: e.target.value })} />
          <Input label="Date" type="date" value={resourcePersonForm.date}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, date: e.target.value })} />
          <SelectInput
            label="Academic Year"
            value={resourcePersonForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setResourcePersonForm({ ...resourcePersonForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={resourcePersonForm.document_link}
            onChange={(url) => setResourcePersonForm({ ...resourcePersonForm, document_link: url })}
            headers={headers}
            section="fdp-resource-person"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addResourcePerson} className="btn-primary">
          {editingResourcePersonId ? "Update Details" : "Add Details"}
        </button>

        {resourcePersonList.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Institute</th>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Academic Year</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Document</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(resourcePersonList).map((record) => (
                  <tr key={record.id} className={`border-t border-gray-200 hover:bg-gray-50 ${record.verification_status === "rejected" ? "bg-red-50" : ""}`}>
                    <td className="px-4 py-2">{record.institute_name || "N/A"}</td>
                    <td className="px-4 py-2">{record.title || "N/A"}</td>
                    <td className="px-4 py-2">{record.academic_year || "N/A"}</td>
                    <td className="px-4 py-2">{formatDateOnly(record.date)}</td>
                    <td className="px-4 py-2">
                      {record.document_link ? (
                        <a href={buildProtectedDocumentUrl(record.document_link)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">View Document</a>
                      ) : "N/A"}
                    </td>
                    <td className="px-4 py-2"><VerificationStatusCell record={record} /></td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit details" variant="edit" onClick={() => startEditResourcePerson(record)} />
                        <ActionIconButton label="Delete details" variant="delete" onClick={() => deleteResourcePerson(record.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

/* ===============================================
   AWARDS & INDUSTRY TAB
=============================================== */
function AwardsIndustry({ headers, showNotification }) {
  const [awardForm, setAwardForm] = useState({ 
    level: "", organization_type: "", award_name: "", award_year: "", academic_year: "", letter_details: "" 
  });
  const [editingAwardId, setEditingAwardId] = useState(null);
  const [industryForm, setIndustryForm] = useState({ 
    industry_name: "", outcome_expected: "", outcome_achieved: "" 
  });
  const [editingIndustryConnectId, setEditingIndustryConnectId] = useState(null);
  const [membershipForm, setMembershipForm] = useState({
    society_name: "",
    membership_type: "",
    category: "",
    membership_number: "",
    is_active: true,
  });
  const [editingMembershipId, setEditingMembershipId] = useState(null);

  const [awards, setAwards] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    loadAwards();
    loadIndustries();
    loadMemberships();
  }, []);

  const loadAwards = async () => {
    try {
      const res = await axios.get("/profile/awards", { headers });
      setAwards(res.data || []);
    } catch (err) {}
  };

  const loadIndustries = async () => {
    try {
      const res = await axios.get("/profile/industry-connect", { headers });
      setIndustries(res.data || []);
    } catch (err) {}
  };

  const loadMemberships = async () => {
    try {
      const res = await axios.get("/profile/professional-memberships", { headers });
      setMemberships(res.data || []);
    } catch (err) {}
  };

  const addAward = async () => {
    try {
      if (editingAwardId) {
        await axios.delete(`/profile/awards/${editingAwardId}`, { headers });
      }
      await axios.post("/profile/awards", awardForm, { headers });
      showNotification(editingAwardId ? "Award updated!" : "Award added!");
      loadAwards();
      setAwardForm({ level: "", organization_type: "", award_name: "", award_year: "", academic_year: "", letter_details: "" });
      setEditingAwardId(null);
    } catch (err) {
      showNotification(editingAwardId ? "Failed to update award" : "Failed to add award", "error");
    }
  };

  const startEditAward = (award) => {
    setAwardForm({
      level: award.level || "",
      organization_type: award.organization_type || "",
      award_name: award.award_name || "",
      award_year: award.award_year || "",
      academic_year: award.academic_year || "",
      letter_details: award.letter_details || "",
    });
    setEditingAwardId(award.id);
  };

  const deleteAward = async (id) => {
    try {
      await axios.delete(`/profile/awards/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadAwards();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addIndustry = async () => {
    try {
      if (editingIndustryConnectId) {
        await axios.delete(`/profile/industry-connect/${editingIndustryConnectId}`, { headers });
      }
      await axios.post("/profile/industry-connect", industryForm, { headers });
      showNotification(editingIndustryConnectId ? "Industry connect updated!" : "Industry connect added!");
      loadIndustries();
      setIndustryForm({ industry_name: "", outcome_expected: "", outcome_achieved: "" });
      setEditingIndustryConnectId(null);
    } catch (err) {
      showNotification(editingIndustryConnectId ? "Failed to update industry connect" : "Failed to add industry connect", "error");
    }
  };

  const startEditIndustryConnect = (industry) => {
    setIndustryForm({
      industry_name: industry.industry_name || "",
      outcome_expected: industry.outcome_expected || "",
      outcome_achieved: industry.outcome_achieved || "",
    });
    setEditingIndustryConnectId(industry.id);
  };

  const deleteIndustry = async (id) => {
    try {
      await axios.delete(`/profile/industry-connect/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadIndustries();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  const addMembership = async () => {
    try {
      if (editingMembershipId) {
        await axios.delete(`/profile/professional-memberships/${editingMembershipId}`, { headers });
      }
      await axios.post("/profile/professional-memberships", membershipForm, { headers });
      showNotification(editingMembershipId ? "Membership updated!" : "Professional membership added!");
      loadMemberships();
      setMembershipForm({ society_name: "", membership_type: "", category: "", membership_number: "", is_active: true });
      setEditingMembershipId(null);
    } catch (err) {
      showNotification(editingMembershipId ? "Failed to update membership" : "Failed to add professional membership", "error");
    }
  };

  const deleteMembership = async (id) => {
    try {
      await axios.delete(`/profile/professional-memberships/${id}`, { headers });
      showNotification("Deleted successfully!");
      loadMemberships();
    } catch (err) {
      showNotification("Failed to delete", "error");
    }
  };

  return (
    <div className="space-y-8">
      {/* Awards */}
      <Section title="Awards & Recognition">
        <DriveFolderHint />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SelectInput
            label="Level"
            value={awardForm.level}
            options={["Department", "Institute", "State", "National", "International"]}
            onChange={(e) => setAwardForm({ ...awardForm, level: e.target.value })}
          />
          <SelectInput
            label="Organization Type"
            value={awardForm.organization_type}
            options={["Government", "Private", "Professional Body", "University", "Industry"]}
            onChange={(e) => setAwardForm({ ...awardForm, organization_type: e.target.value })}
          />
          <Input label="Award Name" value={awardForm.award_name}
            onChange={(e) => setAwardForm({...awardForm, award_name: e.target.value})} />
          <Input label="Year" value={awardForm.award_year}
            onChange={(e) => setAwardForm({...awardForm, award_year: e.target.value})} />
          <SelectInput
            label="Academic Year"
            value={awardForm.academic_year}
            options={ACADEMIC_YEAR_OPTIONS}
            onChange={(e) => setAwardForm({ ...awardForm, academic_year: e.target.value })}
          />
          <DocumentDropbox
            label="Supporting Document"
            value={awardForm.letter_details}
            onChange={(url) => setAwardForm({...awardForm, letter_details: url})}
            headers={headers}
            section="awards"
            showNotification={showNotification}
          />
        </div>
        <button onClick={addAward} className="btn-primary">{editingAwardId ? "Update Award" : "Add Award"}</button>

        {awards.length > 0 && (
          <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Award</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Level</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Organization</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Year</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Academic Year</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(awards).map((award) => (
                  <tr key={award.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-semibold">{award.award_name || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{award.level || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{award.organization_type || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{award.award_year || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{award.academic_year || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        award.verification_status === "verified"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {award.verification_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit award" variant="edit" onClick={() => startEditAward(award)} />
                        <ActionIconButton label="Delete award" variant="delete" onClick={() => deleteAward(award.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Professional Memberships */}
      <Section title="Member of Professional Societies">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="Name of Professional Society" value={membershipForm.society_name}
            onChange={(e) => setMembershipForm({ ...membershipForm, society_name: e.target.value })} />
          <SelectInput
            label="Membership Type"
            value={membershipForm.membership_type}
            options={MEMBERSHIP_TYPE_OPTIONS}
            onChange={(e) => setMembershipForm({ ...membershipForm, membership_type: e.target.value })}
          />
          <SelectInput
            label="Category"
            value={membershipForm.category}
            options={MEMBERSHIP_CATEGORY_OPTIONS}
            onChange={(e) => setMembershipForm({ ...membershipForm, category: e.target.value })}
          />
          <Input label="Number" value={membershipForm.membership_number}
            onChange={(e) => setMembershipForm({ ...membershipForm, membership_number: e.target.value })} />
          <SelectInput
            label="Is Active"
            value={membershipForm.is_active ? "yes" : "no"}
            options={["yes", "no"]}
            onChange={(e) => setMembershipForm({ ...membershipForm, is_active: e.target.value === "yes" })}
          />
        </div>
        <button onClick={addMembership} className="btn-primary">{editingMembershipId ? "Update Membership" : "Add Professional Membership"}</button>

        {memberships.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left">Society</th>
                  <th className="px-4 py-2 text-left">Membership Type</th>
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-left">Number</th>
                  <th className="px-4 py-2 text-left">Active</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(memberships).map((membership) => (
                  <tr key={membership.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">{membership.society_name}</td>
                    <td className="px-4 py-2">{membership.membership_type || "N/A"}</td>
                    <td className="px-4 py-2">{membership.category || "N/A"}</td>
                    <td className="px-4 py-2">{membership.membership_number || "N/A"}</td>
                    <td className="px-4 py-2">{membership.is_active ? "Yes" : "No"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                        membership.verification_status === "verified" ? "bg-green-100 text-green-800" :
                        membership.verification_status === "rejected" ? "bg-red-100 text-red-800" :
                        "bg-yellow-100 text-yellow-800"
                      }`}>
                        {membership.verification_status || "unverified"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit membership" variant="edit" onClick={() => {
                          setMembershipForm({
                            society_name: membership.society_name || "",
                            membership_type: membership.membership_type || "",
                            category: membership.category || "",
                            membership_number: membership.membership_number || "",
                            is_active: Boolean(membership.is_active),
                          });
                          setEditingMembershipId(membership.id);
                        }} />
                        <ActionIconButton label="Delete membership" variant="delete" onClick={() => deleteMembership(membership.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Industry Connect */}
      <Section title="Industry Collaboration">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Input label="Industry Name" value={industryForm.industry_name}
            onChange={(e) => setIndustryForm({...industryForm, industry_name: e.target.value})} />
          <Input label="Outcome Expected" value={industryForm.outcome_expected}
            onChange={(e) => setIndustryForm({...industryForm, outcome_expected: e.target.value})} />
          <Input label="Outcome Achieved" value={industryForm.outcome_achieved}
            onChange={(e) => setIndustryForm({...industryForm, outcome_achieved: e.target.value})} />
        </div>
        <button onClick={addIndustry} className="btn-primary">{editingIndustryConnectId ? "Update Industry Connection" : "Add Industry Connection"}</button>

        {industries.length > 0 && (
          <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Industry</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Expected</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Achieved</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortByYearDesc(industries).map((industry) => (
                  <tr key={industry.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-semibold">{industry.industry_name || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{industry.outcome_expected || "-"}</td>
                    <td className="px-3 py-2 text-gray-700">{industry.outcome_achieved || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        industry.verification_status === "verified"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {industry.verification_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-2">
                        <ActionIconButton label="Edit industry collaboration" variant="edit" onClick={() => startEditIndustryConnect(industry)} />
                        <ActionIconButton label="Delete industry collaboration" variant="delete" onClick={() => deleteIndustry(industry.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

/* ===============================================
   REUSABLE COMPONENTS
=============================================== */
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
      <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b border-gray-200 flex items-center gap-2">
        <span className="w-1 h-6 bg-blue-500 rounded-full"></span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function DriveFolderHint({ message = "Upload supporting documents here. Files are saved to cloud storage." }) {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
      {message}
    </div>
  );
}

function DocumentDropbox({ label, value, onChange, headers, section, showNotification }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("section", section || "general");

      const res = await axios.post("/profile/upload-document", formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });

      if (res.data.document_url) {
        onChange(res.data.document_url);
        setFileName(res.data.file_name || file.name);
        if (showNotification) showNotification("Document uploaded successfully!");
      }
    } catch (err) {
      if (showNotification) showNotification(err.response?.data?.message || "Failed to upload document", "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
        {value ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2 text-green-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">{fileName || "Document uploaded"}</span>
            </div>
            <a
              href={buildProtectedDocumentUrl(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Document
            </a>
            <label className="inline-flex items-center gap-1 text-xs text-gray-500 cursor-pointer hover:text-blue-600">
              Replace file
              <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" disabled={uploading} />
            </label>
          </div>
        ) : (
          <label className={`cursor-pointer ${uploading ? "opacity-50" : ""}`}>
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-600">
                {uploading ? "Uploading..." : "Click to upload document"}
              </span>
              <span className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG (max 10MB)</span>
            </div>
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" disabled={uploading} />
          </label>
        )}
      </div>
    </div>
  );
}

function DocumentUploadInput({ label, onChange }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <input
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        onChange={onChange}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
      />
    </div>
  );
}

function SelectInput({ label, value, onChange, options = [], className = "", required = false, disabled = false }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white text-sm"
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", className = "", placeholder = "", required = false, disabled = false }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
      />
    </div>
  );
}

function VerificationStatusPill({ status }) {
  const verified = status === "verified";

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${verified ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
      {verified ? "Verified" : "Unverified"}
    </span>
  );
}

function Card({ children, className = "", verificationStatus }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${className}`}>
      {verificationStatus !== undefined && (
        <div className="mb-3">
          <VerificationStatusPill status={verificationStatus} />
        </div>
      )}
      {children}
    </div>
  );
}

function ActionIconButton({ label, onClick, variant = "edit" }) {
  const isDelete = variant === "delete";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-full border p-2 transition-colors ${
        isDelete
          ? "border-red-200 text-red-600 hover:bg-red-50"
          : "border-blue-200 text-blue-600 hover:bg-blue-50"
      }`}
      aria-label={label}
      title={label}
    >
      {isDelete ? (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
          <path d="M6 6l1 14h10l1-14" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      ) : (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
        </svg>
      )}
    </button>
  );
}

export default Profile;
